import React, { useEffect, useState, useRef, cloneElement, lazy, memo } from 'react';
import { motion, AnimatePresence, useInView, useReducedMotion } from 'framer-motion';
import { ArrowRight, Check, HashIcon, Bold, Italic, Strikethrough, Link2, Plus, Smile, AtSign, PlusIcon, SmileIcon, CheckIcon, CalendarCheckIcon, Sparkles, X } from 'lucide-react';
import { WaitlistCTA } from '../components/WaitlistCTA';
import { useSuppressFloatingCta } from '../components/floatingCtaContext';
import { ClosingCTA } from '../components/ClosingCTA';
// Authentic four-color Slack mark (the icon CDN only serves a flat monochrome
// version), so the composer reads unmistakably as Slack.
function SlackMark({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" className={className} aria-hidden="true" data-id="element-8729" data-mp="226hj9i0xishf">
      <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z" fill="#E01E5A" data-id="element-8730" data-mp="277faek3x2wv2" />

      <path d="M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.527 2.527 0 0 1 2.521 2.521 2.527 2.527 0 0 1-2.521 2.521H2.522A2.527 2.527 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312z" fill="#36C5F0" data-id="element-8731" data-mp="xoimmyil796k" />

      <path d="M18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522v-2.521zm-1.272 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.161 0a2.528 2.528 0 0 1 2.523 2.522v6.312z" fill="#2EB67D" data-id="element-8732" data-mp="opx433d47u5s" />

      <path d="M15.161 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.161 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.272a2.527 2.527 0 0 1-2.52-2.521 2.526 2.526 0 0 1 2.52-2.522h6.317A2.527 2.527 0 0 1 24 15.163a2.528 2.528 0 0 1-2.522 2.521h-6.317z" fill="#ECB22E" data-id="element-8733" data-mp="1bmh1e50tl7ml" />
    </svg>;
}
// ---------------------------------------------------------------------------
// E-COMMERCE HERO — mirrors the main home page hero (HowItWorksConnections):
// headline -> animated @Giga typewriter prompt card -> CTA -> trust marquee.
// A dark connections logo wall follows. All workflows + logos are e-commerce
// specific. Built fully inline so no shared component behavior changes.
// ---------------------------------------------------------------------------
// E-commerce tool logos for the "Plugs into your store's stack" marquee,
// rendered as uniform off-white silhouettes on dark tiles (same treatment as
// the main home page connections wall).
const ECOM_LOGOS: {
  slug: string;
  name: string;
}[] = [{
  slug: 'shopify',
  name: 'Shopify'
}, {
  slug: 'stripe',
  name: 'Stripe'
}, {
  slug: 'klaviyo',
  name: 'Klaviyo'
}, {
  slug: 'gorgias',
  name: 'Gorgias'
}, {
  slug: 'meta',
  name: 'Meta Ads'
}, {
  slug: 'googleads',
  name: 'Google Ads'
}, {
  slug: 'tiktok',
  name: 'TikTok'
}, {
  slug: 'mailchimp',
  name: 'Mailchimp'
}, {
  slug: 'amazon',
  name: 'Amazon'
}, {
  slug: 'quickbooks',
  name: 'QuickBooks'
}, {
  slug: 'googlesheets',
  name: 'Google Sheets'
}, {
  slug: 'zendesk',
  name: 'Zendesk'
}, {
  slug: 'gmail',
  name: 'Gmail'
}, {
  slug: 'recharge',
  name: 'Recharge'
}, {
  slug: 'aftership',
  name: 'AfterShip'
}, {
  slug: 'slack',
  name: 'Slack'
}];
type PromptSegment = {
  type: 'text';
  content: string;
} | {
  type: 'chip';
  slug: string;
  name: string;
} | {
  type: 'mention';
  name: string;
};
type Workflow = {
  segments: PromptSegment[];
};
// E-commerce operator workflows — the everyday multi-step jobs a DTC team
// instantly recognizes: damaged-order refunds, the weekly sales recap, ad
// spend rollups, low-stock reorders.
const WORKFLOWS: Workflow[] = [{
  segments: [{
    type: 'mention',
    name: 'Giga'
  }, {
    type: 'text',
    content: ' why are sales down today? Check '
  }, {
    type: 'chip',
    slug: 'shopify',
    name: 'Shopify'
  }, {
    type: 'text',
    content: ' and '
  }, {
    type: 'chip',
    slug: 'meta',
    name: 'Meta Ads'
  }]
}, {
  segments: [{
    type: 'mention',
    name: 'Giga'
  }, {
    type: 'text',
    content: ' what is the complete history of customer #8492 across '
  }, {
    type: 'chip',
    slug: 'shopify',
    name: 'Shopify'
  }, {
    type: 'text',
    content: ', '
  }, {
    type: 'chip',
    slug: 'gorgias',
    name: 'Gorgias'
  }, {
    type: 'text',
    content: ', and '
  }, {
    type: 'chip',
    slug: 'klaviyo',
    name: 'Klaviyo'
  }, {
    type: 'text',
    content: '?'
  }]
}, {
  segments: [{
    type: 'mention',
    name: 'Giga'
  }, {
    type: 'text',
    content: ' which products are about to stock out based on velocity in '
  }, {
    type: 'chip',
    slug: 'shopify',
    name: 'Shopify'
  }, {
    type: 'text',
    content: '?'
  }]
}, {
  segments: [{
    type: 'mention',
    name: 'Giga'
  }, {
    type: 'text',
    content: " what did we learn from last year's Black Friday in "
  }, {
    type: 'chip',
    slug: 'notion',
    name: 'Notion'
  }, {
    type: 'text',
    content: ' and '
  }, {
    type: 'chip',
    slug: 'slack',
    name: 'Slack'
  }, {
    type: 'text',
    content: '?'
  }]
}];
function logoSrc(slug: string) {
  return `https://cdn.simpleicons.org/${slug}`;
}
function ToolChip({
  slug,
  name
}: {
  slug: string;
  name: string;
}) {
  return <motion.span initial={{
    opacity: 0,
    scale: 0.9
  }} animate={{
    opacity: 1,
    scale: 1
  }} transition={{
    duration: 0.2,
    ease: 'easeOut'
  }} className="relative mr-1 inline-flex items-center gap-1.5 pl-2 pr-2.5 py-1 rounded-xl bg-[#F4F0EA] border border-neutral-200 text-sm font-medium text-[#2B211A] align-middle shadow-sm overflow-hidden" data-id="element-8734" data-mp="2h8t5qai0tiet">
      <img src={logoSrc(slug)} alt={name} className="relative w-3.5 h-3.5 object-contain" onError={e => {
      const img = e.currentTarget as HTMLImageElement;
      if (!img.dataset.fallback) {
        img.dataset.fallback = '1';
        img.src = `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${slug}.svg`;
      } else {
        img.style.visibility = 'hidden';
      }
    }} data-id="element-8735" data-mp="23fjzujta7f11" />

      <span className="relative" data-id="element-8736" data-mp="1osv0qj2xz3v6">{name}</span>
    </motion.span>;
}
function GigaMention({
  name
}: {
  name: string;
}) {
  return <motion.span initial={{
    opacity: 0,
    scale: 0.9
  }} animate={{
    opacity: 1,
    scale: 1
  }} transition={{
    duration: 0.2,
    ease: 'easeOut'
  }} role="link" className="inline rounded-[3px] pl-1 pr-0.5 text-[#EA580C] font-semibold cursor-pointer transition-colors hover:underline bg-[#EA580C]/12" data-id="element-8737" data-mp="1ewi71l7ecq0f">
      @{name}
    </motion.span>;
}
function promptUnitCount(segments: PromptSegment[]) {
  return segments.reduce((sum, seg) => sum + (seg.type === 'text' ? seg.content.length : 1), 0);
}
function renderTyped(segments: PromptSegment[], revealed: number): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let cursor = 0;
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    if (seg.type === 'text') {
      const shown = Math.max(0, Math.min(seg.content.length, revealed - cursor));
      if (shown > 0) {
        out.push(<span key={i} data-id="element-8738" data-mp="9vse9rd6hrup">{seg.content.slice(0, shown)}</span>);
      }
      cursor += seg.content.length;
    } else if (seg.type === 'mention') {
      if (revealed > cursor) {
        out.push(<GigaMention key={i} name={seg.name} data-id="element-8739" data-mp="jog94gklllqs" />);
      }
      cursor += 1;
    } else {
      if (revealed > cursor) {
        out.push(<ToolChip key={i} slug={seg.slug} name={seg.name} data-id="element-8740" data-mp="3n4ep005cqt9l" />);
      }
      cursor += 1;
    }
    if (cursor >= revealed) break;
  }
  return out;
}
// Slack-green paper-plane send button that lives in the composer action row.
// Pulses gently while there's a message being typed.
function PromptSendButton({
  active
}: {
  active: boolean;
}) {
  const reducedMotion = useReducedMotion();
  return <motion.span className={`flex items-center justify-center w-9 h-9 md:w-10 md:h-10 rounded-lg shadow-sm ${active ? 'bg-[#007A5A]' : 'bg-neutral-200'}`} animate={reducedMotion || !active ? undefined : {
    scale: [1, 1.08, 1]
  }} transition={reducedMotion ? undefined : {
    duration: 1.6,
    repeat: Infinity,
    ease: 'easeInOut'
  }} data-id="element-8741" data-mp="1hd9m1sbm9xmf">
      <svg viewBox="0 0 24 24" className="w-[15px] h-[15px] md:w-[17px] md:h-[17px] -ml-0.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" data-id="element-8742" data-mp="3fs7b4d71lo66">
        <path d="m22 2-7 20-4-9-9-4Z" data-id="element-8743" data-mp="3pia4iei2t451" />
        <path d="M22 2 11 13" data-id="element-8744" data-mp="3pleczo2kqm1i" />
      </svg>
    </motion.span>;
}
function TypewriterInputCard({
  reducedMotion
}: {
  reducedMotion: boolean;
}) {
  const [workflowIndex, setWorkflowIndex] = useState(0);
  const workflow = WORKFLOWS[workflowIndex];
  const totalUnits = promptUnitCount(workflow.segments);
  const [revealed, setRevealed] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 767px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  const containerRef = useRef<HTMLDivElement>(null);
  const inView = useInView(containerRef);
  useEffect(() => {
    if (reducedMotion) {
      setRevealed(totalUnits);
      return;
    }
    if (!inView) return;
    if (revealed < totalUnits) {
      const atChip = (() => {
        let cursor = 0;
        for (const seg of workflow.segments) {
          const len = seg.type === 'text' ? seg.content.length : 1;
          if (revealed >= cursor && revealed < cursor + len) {
            return seg.type === 'chip';
          }
          cursor += len;
        }
        return false;
      })();
      const charMs = isMobile ? 11 : 22;
      const chipMs = isMobile ? 80 : 140;
      const id = setTimeout(() => setRevealed(r => r + 1), atChip ? chipMs : charMs);
      return () => clearTimeout(id);
    } else {
      const id = setTimeout(() => {
        setWorkflowIndex(i => (i + 1) % WORKFLOWS.length);
        setRevealed(0);
      }, 1800);
      return () => clearTimeout(id);
    }
  }, [reducedMotion, inView, revealed, totalUnits, workflow.segments, workflowIndex, isMobile]);
  const isTyping = !reducedMotion && revealed < totalUnits;
  return <motion.div ref={containerRef} initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-100px'
  }} transition={{
    duration: 0.6
  }} className="relative bg-white rounded-[24px] border border-neutral-200/80 shadow-2xl overflow-hidden max-w-[860px] mx-auto text-left" data-id="element-8745" data-mp="2f0stucvgpp88">
      {/* Slack channel header (aubergine) — the four-color Slack mark + the open
                           channel, so the whole card reads unmistakably as a message you'd send
                           to Giga in your store's ops channel. */}
      <div className="flex items-center gap-2.5 bg-[#3F0E40] px-4 md:px-5 py-3" data-id="element-8746" data-mp="13fchmrp0qb3i">
        <span className="flex items-center justify-center w-6 h-6 rounded-md bg-white shrink-0" data-id="element-8747" data-mp="3f14phjpmaxup">
          <SlackMark className="w-3.5 h-3.5" data-id="element-8748" data-mp="3k0r0rnlvy40v" />
        </span>
        <span className="flex items-center gap-1 text-sm md:text-base font-bold text-white truncate" data-id="element-8749" data-mp="3k2by1kzl412f">
          <HashIcon size={15} className="text-white/60 shrink-0" data-id="element-8750" data-mp="3aao9psybr056" />
          ops
        </span>
        <span className="ml-auto flex items-center gap-1.5 text-xs font-semibold text-white/55 shrink-0" data-id="element-8751" data-mp="3a8bl4wy29rj5">
          <span className="inline-block w-2 h-2 rounded-full bg-[#2EB67D]" data-id="element-8752" data-mp="319zuww13wvr8" />6
        </span>
      </div>

      {/* The composer the message is being typed into. */}
      <div className="p-4 md:p-6" data-id="element-8753" data-mp="2mejwkethudp1">
        <div className="rounded-xl border border-neutral-300/80 overflow-hidden" data-id="element-8754" data-mp="2rbtqa6nx1bio">
          {/* Slack's signature B / I / S / link formatting toolbar. */}
          <div className="flex items-center gap-3 border-b border-neutral-200 px-3.5 py-2 text-neutral-400" data-id="element-8755" data-mp="klvo9r1zt02e">
            <Bold size={13} strokeWidth={2.75} data-id="element-8756" data-mp="pjnqbdwcq2kb" />
            <Italic size={13} strokeWidth={2.75} data-id="element-8757" data-mp="o3t7ndnpqbiq" />
            <Strikethrough size={13} strokeWidth={2.75} data-id="element-8758" data-mp="o4kzqei3pr49" />
            <span className="h-3.5 w-px bg-neutral-200" data-id="element-8759" data-mp="j3xa9x9itic5" />
            <Link2 size={13} strokeWidth={2.5} data-id="element-8760" data-mp="j6jj0085e3vy" />
          </div>

          {/* The message itself — types out one unit at a time. Fixed height so
           the card never jumps as workflows swap. */}
          <div className="px-3.5 pt-3 text-sm md:text-lg font-medium text-[#2B211A] leading-[2.2] md:leading-[2.1] h-[120px] md:h-[112px] overflow-hidden" data-id="element-8761" data-mp="a5v1uwngjneu">
            {renderTyped(workflow.segments, revealed)}
            {isTyping && <span aria-hidden="true" className="inline-block w-[2px] h-[1em] bg-[#EA580C] align-middle ml-0.5 animate-pulse" data-id="element-8762" data-mp="x3g6pssyz58m" />}
          </div>

          {/* Composer action row — Slack's left icons + the green send button. */}
          <div className="flex items-center justify-between px-3.5 pb-2.5 pt-1" data-id="element-8763" data-mp="1pl2bopc3f7l4">
            <span className="flex items-center gap-3 text-neutral-300" data-id="element-8764" data-mp="1ktahm4tqajq8">
              <Plus size={16} strokeWidth={2.5} data-id="element-8765" data-mp="33gk5nfteg6py" />
              <Smile size={16} strokeWidth={2.5} data-id="element-8766" data-mp="33hv8ndf0tag0" />
              <AtSign size={16} strokeWidth={2.5} data-id="element-8767" data-mp="38f3rzhbwnkiu" />
            </span>
            <span className={isTyping || revealed > 0 ? 'text-white' : 'text-neutral-400'} data-id="element-8768" data-mp="38hhntseet0rg">
              <PromptSendButton active={revealed > 0} data-id="element-8769" data-mp="3cfnsmuvot69q" />
            </span>
          </div>
        </div>
      </div>
    </motion.div>;
}
// Dark-tile e-commerce logo marquee — mirrors the main home page connections
// wall (uniform off-white silhouettes, two rows scrolling opposite ways).
function EcomLogoTile({
  logo
}: {
  logo: {
    slug: string;
    name: string;
  };
}) {
  return <div className="group/tile relative shrink-0 flex items-center justify-center w-[80px] h-[80px] md:w-[100px] md:h-[100px] rounded-2xl md:rounded-[24px] ring-1 ring-white/[0.08] shadow-[0_10px_30px_-12px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.07)] transition-all duration-300 hover:scale-[1.06] hover:ring-white/20" style={{
    backgroundImage: 'linear-gradient(155deg, #2B2018 0%, #211810 55%, #1C140D 100%)'
  }} title={logo.name} data-id="element-8770" data-mp="3o2k37rsru">
      <span aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-2xl md:rounded-[24px] opacity-60" style={{
      backgroundImage: 'linear-gradient(160deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0) 45%)'
    }} data-id="element-8771" data-mp="3rj70r07c3ixr" />

      <img src={logoSrc(logo.slug)} alt={logo.name} className="relative z-10 w-8 h-8 md:w-10 md:h-10 object-contain opacity-90 [filter:brightness(0)_invert(0.92)]" loading="lazy" onError={e => {
      const img = e.currentTarget as HTMLImageElement;
      if (!img.dataset.fallback) {
        img.dataset.fallback = '1';
        img.src = `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${logo.slug}.svg`;
      } else {
        img.style.visibility = 'hidden';
      }
    }} data-id="element-8772" data-mp="54nlz2eg61qr" />
    </div>;
}
function EcomLogoMarquee({
  reducedMotion
}: {
  reducedMotion: boolean;
}) {
  const row1 = ECOM_LOGOS.slice(0, 8);
  const row2 = ECOM_LOGOS.slice(8, 16);
  return <div className="relative w-full overflow-hidden flex flex-col gap-4 md:gap-6" style={{
    WebkitMaskImage: 'linear-gradient(to right, transparent 0, #000 15%, #000 85%, transparent 100%)',
    maskImage: 'linear-gradient(to right, transparent 0, #000 15%, #000 85%, transparent 100%)'
  }} data-id="element-8773" data-mp="15azt3ljt2750">
      <motion.div className="flex gap-4 md:gap-6 w-max" animate={reducedMotion ? undefined : {
      x: ['0%', '-50%']
    }} transition={reducedMotion ? undefined : {
      duration: 50,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-8774" data-mp="10c6h5xk62o6y">
        {[...row1, ...row1, ...row1, ...row1].map((logo, i) => <EcomLogoTile key={`r1-${logo.slug}-${i}`} logo={logo} data-id="element-8775" data-mp="1f9ypo5raqcbn" data-mpi={i} />)}
      </motion.div>
      <motion.div className="flex gap-4 md:gap-6 w-max" animate={reducedMotion ? undefined : {
      x: ['-50%', '0%']
    }} transition={reducedMotion ? undefined : {
      duration: 60,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-8776" data-mp="1jbalwu4odvst">
        {[...row2, ...row2, ...row2, ...row2].map((logo, i) => <EcomLogoTile key={`r2-${logo.slug}-${i}`} logo={logo} data-id="element-8777" data-mp="2lnlgp8c6i41x" data-mpi={i} />)}
      </motion.div>
    </div>;
}
// --- CUSTOM E-COMMERCE HERO ---
function EcommerceHero() {
  const reducedMotion = useReducedMotion();
  return <section className="relative w-full overflow-hidden bg-[#FAF8F4] text-[#2B211A] pt-32 pb-10 md:pt-44 md:pb-12" data-id="element-8778" data-mp="2cmx50ka9cfs7">
      <div className="absolute inset-0 dot-matrix opacity-40 pointer-events-none z-0" data-id="element-8779" data-mp="27oc1aavsrdz0" />
      <div className="absolute inset-0 opacity-[0.03] mix-blend-multiply pointer-events-none z-0" style={{
      backgroundImage: 'url("https://grainy-gradients.vercel.app/noise.svg")'
    }} data-id="element-8780" data-mp="22ohbkanrcufj" />

      <div className="relative z-10 w-full max-w-[1280px] mx-auto px-6 md:px-10 text-center" data-id="element-8781" data-mp="1tw5x7n5x4xfx">
        <h2 className="font-title font-medium tracking-tight max-w-4xl mx-auto mt-0 leading-[1.18] text-[#2B211A] text-4xl sm:text-6xl md:text-7xl mb-6 md:mb-8" data-id="element-8782" data-mp="1oxb6lz1pm4l9">
          Your store runs on ten tools. <br className="hidden md:block" data-id="element-8783" data-mp="1ez53je462set" />
          Giga runs all ten.
        </h2>

        <p className="text-base md:text-lg text-[#6B5D52] leading-relaxed max-w-3xl mx-auto mt-0 mb-8 md:mb-12" data-id="element-8784" data-mp="1f1806gztrjsi">
          Drop <span className="font-semibold text-[#EA580C]" data-id="element-8785" data-mp="19ub4t59mzbp3">@Giga</span> into
          Slack to ask questions and get instant, cross-tool answers. It's like
          having one employee who has read every ticket, order, and dashboard.
        </p>

        {/* Animated @Giga prompt card — mirrors the main home page hero */}
        <TypewriterInputCard reducedMotion={!!reducedMotion} data-id="element-8786" data-mp="m5i1gnqoq1kg" />

        <div className="mt-10 md:mt-12 flex justify-center" data-id="element-8787" data-mp="m2vvutnctw4v">
          <WaitlistCTA className="group inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-[#EA580C] hover:bg-[#d24e09] text-white font-bold text-lg shadow-lg shadow-[#EA580C]/20 transition-all hover:scale-105" data-id="element-8788" data-mp="r4l5q5yuc3rd">
            Get started for free
            <ArrowRight size={20} strokeWidth={2.5} className="transition-transform duration-300 group-hover:translate-x-0.5" data-id="element-8789" data-mp="3eqxy2tdp5dz" />
          </WaitlistCTA>
        </div>

        <div className="mt-12 md:mt-16 flex flex-col items-center" data-id="element-8790" data-mp="8az62w95rybn">
          <p className="text-[10px] md:text-xs font-semibold tracking-[4px] uppercase mb-8 text-neutral-400" data-id="element-8791" data-mp="1mkagw6y5etpg">
            Trusted by fast-moving brands
          </p>
          <TrustBrandMarquee reducedMotion={!!reducedMotion} data-id="element-8792" data-mp="1wi854ug0cqi2" />
        </div>

        <div className="mt-12 md:mt-16 w-full border-t border-neutral-200/60" data-id="element-8793" data-mp="1riva3mezgd4e" />
      </div>
    </section>;
}
// Continuously scrolling brand wordmarks beneath the hero — reads like a real
// "trusted by" carousel rather than a static, half-finished text row.
const TRUST_BRANDS = ['Glossier', 'Allbirds', 'Gymshark', 'Brooklinen', 'Olipop', 'Magic Spoon', 'Caraway', 'Ridge'];
function TrustBrandMarquee({
  reducedMotion
}: {
  reducedMotion: boolean;
}) {
  return <div className="relative w-full max-w-4xl overflow-hidden" style={{
    WebkitMaskImage: 'linear-gradient(to right, transparent 0, #000 12%, #000 88%, transparent 100%)',
    maskImage: 'linear-gradient(to right, transparent 0, #000 12%, #000 88%, transparent 100%)'
  }} data-id="element-8794" data-mp="2aa4tqoigwi9p">
      <motion.div className="flex items-center gap-x-10 sm:gap-x-14 md:gap-x-16 w-max" animate={reducedMotion ? undefined : {
      x: ['0%', '-50%']
    }} transition={reducedMotion ? undefined : {
      duration: 32,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-8795" data-mp="33udov6nq7e9l">
        {[...TRUST_BRANDS, ...TRUST_BRANDS].map((name, i) => <span key={`${name}-${i}`} className="whitespace-nowrap font-title text-base sm:text-lg font-semibold tracking-tight text-[#2B211A]/65" data-id="element-8796" data-mp="2ysep06r72zhe" data-mpi={i}>
            {name}
          </span>)}
      </motion.div>
    </div>;
}
// --- E-COMMERCE CONNECTIONS / LOGO WALL ---
// Mirrors the dark connections beat on the main home page, but with the
// e-commerce stack.
function EcommerceConnections() {
  const reducedMotion = useReducedMotion();
  return <section className="relative w-full overflow-hidden bg-[#1A1410] py-24 md:py-32" data-id="element-8797" data-mp="216r2cdkjjvj2">
      <div className="pointer-events-none absolute inset-0" aria-hidden="true" style={{
      background: 'radial-gradient(60% 55% at 50% 32%, rgba(234,88,12,0.05) 0%, transparent 70%)'
    }} data-id="element-8798" data-mp="265kgm87njqka" />

      <div className="relative z-10 mx-auto flex max-w-[1280px] flex-col items-center px-6 text-center md:px-10" data-id="element-8799" data-mp="25cj7knwdz7bz">
        <span className="block text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-5 md:mb-6" data-id="element-8800" data-mp="1vc8xhc5neg11">
          Connections
        </span>
        <h2 className="max-w-4xl text-3xl md:text-5xl font-title font-medium leading-[1.1] tracking-tight text-[#FAF8F4]" data-id="element-8801" data-mp="20bltamehyxfr">
          Plugs into the stack you already run
        </h2>
        <p className="mt-5 max-w-2xl text-lg leading-relaxed text-white/65 md:mt-6 md:text-xl" data-id="element-8802" data-mp="2syps29mzyfj3">
          Shopify, Stripe, Klaviyo, Gorgias, Meta Ads, and more — Giga pulls
          context between them and takes action, with you in control.
        </p>
      </div>

      <div className="relative z-10 mx-auto mt-14 w-full max-w-6xl md:mt-20" data-id="element-8803" data-mp="36zseinhwlruj">
        <EcomLogoMarquee reducedMotion={!!reducedMotion} data-id="element-8804" data-mp="36xpedan1eic3" />
      </div>
    </section>;
}
// --- CUSTOM E-COMMERCE PROBLEM SECTION ---
function EcomBehindWindow({
  className,
  rotate,
  z,
  accent,
  label,
  children
}: {
  className: string;
  rotate: number;
  z: number;
  accent: string;
  label: string;
  children: React.ReactNode;
}) {
  return <div className={`absolute w-[260px] rounded-2xl bg-white border border-black/5 shadow-[0_24px_60px_-24px_rgba(43,33,26,0.45)] overflow-hidden ${className}`} style={{
    transform: `rotate(${rotate}deg)`,
    zIndex: z
  }} aria-hidden="true" data-id="element-8805" data-mp="1a01kj9ajpixc">
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-black/[0.06] bg-[#FBFAF8]" data-id="element-8806" data-mp="he8rhrf1xinr">
        <span className="w-2 h-2 rounded-full bg-[#FF5F57]" data-id="element-8807" data-mp="cfnqvqvw0wys" />
        <span className="w-2 h-2 rounded-full bg-[#FEBC2E]" data-id="element-8808" data-mp="qenf4xfhn9uf" />
        <span className="w-2 h-2 rounded-full bg-[#28C840]" data-id="element-8809" data-mp="maz7yfurqg9p" />
        <span className="ml-2 text-[9px] font-semibold text-neutral-400 truncate" data-id="element-8810" data-mp="mdlecc5xapqc">
          {label}
        </span>
        <span className="ml-auto w-3.5 h-3.5 rounded-[5px]" style={{
        backgroundColor: accent
      }} data-id="element-8811" data-mp="1xqnvb8bxphb1" />
      </div>
      <div className="p-3" data-id="element-8812" data-mp="ug9phqel4bcf">{children}</div>
    </div>;
}
function EcomLine({
  w,
  dark
}: {
  w: string;
  dark?: boolean;
}) {
  return <div className={`h-1.5 rounded ${dark ? 'bg-neutral-300' : 'bg-neutral-100'}`} style={{
    width: w
  }} data-id="element-8813" data-mp="zd9udxzp781y" />;
}
const ECOM_TABS = [{
  slug: 'shopify',
  title: 'Orders',
  active: true
}, {
  slug: 'stripe',
  title: 'Payments'
}, {
  slug: 'gorgias',
  title: 'Tickets'
}, {
  slug: 'klaviyo',
  title: 'Campaigns'
}, {
  slug: 'meta',
  title: 'Ads Manager'
}, {
  slug: 'googleads',
  title: 'Google Ads'
}, {
  slug: 'googlesheets',
  title: 'Inventory.xlsx'
}, {
  slug: 'slack',
  title: '#cx-escalations'
}, {
  slug: 'zendesk',
  title: 'Support'
}, {
  slug: 'notion',
  title: 'Q3 Planning'
}, {
  slug: 'gmail',
  title: 'Inbox (12)'
}];
function EcommerceProblem() {
  const reduced = !!useReducedMotion();
  return <section className="relative w-full overflow-hidden bg-[#1A1410] md:bg-[#F4F0EA] py-24 md:py-32 border-t border-neutral-200/70" data-id="element-8814" data-mp="3p2c6lpowfjbh">
      <div aria-hidden="true" className="hidden md:block absolute inset-y-0 left-0 w-1/2 overflow-hidden bg-[#1A1410]" data-id="element-8815" data-mp="3lmr38apdm85k" />

      <div aria-hidden="true" className="hidden md:block absolute inset-y-0 right-0 w-1/2 overflow-hidden bg-white" data-id="element-8816" data-mp="3go7ek2jeei9i">
        <div className="absolute inset-0" style={{
        backgroundImage: 'radial-gradient(rgba(43,33,26,0.10) 1px, transparent 1px)',
        backgroundSize: '16px 16px',
        maskImage: 'radial-gradient(120% 100% at 65% 45%, #000 25%, transparent 88%)',
        WebkitMaskImage: 'radial-gradient(120% 100% at 65% 45%, #000 25%, transparent 88%)'
      }} data-id="element-8817" data-mp="3cjzqm7krl6cc" />

        <div className="absolute inset-0" style={{
        background: 'radial-gradient(55% 55% at 60% 42%, rgba(234,88,12,0.05) 0%, transparent 70%)'
      }} data-id="element-8818" data-mp="2swsc89rmg0y9" />
      </div>

      <div aria-hidden="true" className="md:hidden pointer-events-none absolute inset-0 overflow-hidden" data-id="element-8819" data-mp="2jwvpwespfe6d">
        <div className="absolute inset-0" style={{
        background: 'radial-gradient(70% 50% at 50% 30%, rgba(234,88,12,0.12) 0%, transparent 70%)'
      }} data-id="element-8820" data-mp="2ey2gaxgjg7bh" />

        <div className="absolute inset-0" style={{
        backgroundImage: 'radial-gradient(rgba(255,255,255,0.07) 1px, transparent 1px)',
        backgroundSize: '16px 16px',
        maskImage: 'radial-gradient(120% 85% at 50% 45%, #000 28%, transparent 86%)',
        WebkitMaskImage: 'radial-gradient(120% 85% at 50% 45%, #000 28%, transparent 86%)'
      }} data-id="element-8821" data-mp="iwqwazbv8fai" />
      </div>

      <div className="relative z-10 mx-auto max-w-[1280px] px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center" data-id="element-8822" data-mp="3d4v47d2qzn">
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }} className="relative z-10 max-w-xl text-center md:text-left" data-id="element-8823" data-mp="43f1v025iw8l">
          <h2 className="text-3xl md:text-5xl font-title font-medium leading-[1.1] tracking-tight text-[#FAF8F4]" data-id="element-8824" data-mp="1pjco5e4p1wt0">
            Every answer exists. <br className="md:hidden" data-id="element-8825" data-mp="3a1eqj3qy3xe8" />
            <span className="text-[#EA580C]" data-id="element-8826" data-mp="352al8o756jt6">It's just fragmented.</span>
          </h2>
          <p className="mt-5 md:mt-6 max-w-[17rem] mx-auto md:max-w-xl md:mx-0 text-base md:text-lg text-white/65 font-medium leading-relaxed [text-wrap:balance] md:[text-wrap:initial]" data-id="element-8827" data-mp="3jqyakcqe9xao">
            Almost every important question requires opening{' '}
            <span className="whitespace-nowrap" data-id="element-8828" data-mp="3sqbq9n543i3u">5-10 tabs</span>, searching
            Slack, asking teammates, and manually piecing together an answer.
            Giga connects it all into a shared AI workspace with memory.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }} className="relative h-[440px] md:h-[500px] w-full flex items-center justify-center" data-id="element-8829" data-mp="1lldwzzqj62g">
          <motion.div className="absolute left-2 top-2 md:left-4" animate={reduced ? undefined : {
          y: [0, -7, 0]
        }} transition={reduced ? undefined : {
          duration: 6,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-8830" data-mp="2t36ttm3rjfoe">
            <EcomBehindWindow className="" rotate={-8} z={1} accent="#8B5CF6" label="Slack — #cx-escalations" data-id="element-8831" data-mp="371ekjca7hkr4">
              <div className="space-y-2.5" data-id="element-8832" data-mp="3bz6nd1ce2ra3">
                {[{
                w: '70%',
                who: '#EA580C'
              }, {
                w: '52%',
                who: '#3B82F6'
              }, {
                w: '64%',
                who: '#10B981'
              }].map((m, i) => <div key={i} className="flex items-start gap-2" data-id="element-8833" data-mp="3pps0saj2q90l" data-mpi={i}>
                    <span className="w-5 h-5 rounded-md shrink-0" style={{
                  backgroundColor: m.who,
                  opacity: 0.85
                }} data-id="element-8834" data-mp="3uqpot3qrlxa0" />

                    <div className="flex-1 space-y-1 pt-0.5" data-id="element-8835" data-mp="3hip1h2323t6">
                      <EcomLine w="38%" dark data-id="element-8836" data-mp="1o3ie22a4fe31" />
                      <EcomLine w={m.w} data-id="element-8837" data-mp="1j343ach4xq1b" />
                    </div>
                  </div>)}
                <div className="mt-1 h-6 rounded-lg border border-neutral-200 bg-neutral-50" data-id="element-8838" data-mp="1e3rh7ty38mzs" />
              </div>
            </EcomBehindWindow>
          </motion.div>

          <motion.div className="absolute right-0 top-2 md:-right-4" animate={reduced ? undefined : {
          y: [0, 8, 0]
        }} transition={reduced ? undefined : {
          duration: 7,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 0.6
        }} data-id="element-8839" data-mp="1967vk8z297xt">
            <EcomBehindWindow className="" rotate={7} z={1} accent="#10B981" label="Inventory.xlsx" data-id="element-8840" data-mp="1x1arf0wa8bns">
              <div className="grid grid-cols-4 gap-px bg-neutral-200 rounded overflow-hidden" data-id="element-8841" data-mp="1s2r2qsqb0lrq">
                {Array.from({
                length: 16
              }).map((_, i) => <div key={i} className={`h-5 ${i % 4 === 0 ? 'bg-[#F4F0EA]' : i === 5 || i === 10 ? 'bg-[#EA580C]/15' : 'bg-white'} flex items-center px-1`} data-id="element-8842" data-mp="2krof6f7u3ock" data-mpi={i}>
                    <span className="h-1 w-full rounded bg-neutral-200" data-id="element-8843" data-mp="1t2scewnlx7qn" />
                  </div>)}
              </div>
            </EcomBehindWindow>
          </motion.div>

          <motion.div className="absolute -bottom-2 left-10" animate={reduced ? undefined : {
          y: [0, 6, 0]
        }} transition={reduced ? undefined : {
          duration: 6.5,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 1.1
        }} data-id="element-8844" data-mp="1k5i15bashilq">
            <EcomBehindWindow className="" rotate={4} z={1} accent="#3B82F6" label="Meta Ads — Campaigns" data-id="element-8845" data-mp="2gt66sttnzioe">
              <div className="grid grid-cols-3 gap-2" data-id="element-8846" data-mp="22tmzg38ogbd5">
                {[2, 3, 1].map((n, col) => <div key={col} className="space-y-1.5" data-id="element-8847" data-mp="27sq4hnonqhl4" data-mpi={col}>
                    <EcomLine w="60%" dark data-id="element-8848" data-mp="27vcedtz0t96i" />
                    {Array.from({
                  length: n
                }).map((_, i) => <div key={i} className="rounded-md border border-neutral-200 bg-neutral-50 p-1.5 space-y-1" data-id="element-8849" data-mp="we2whbvjip7b" data-mpi={i}>
                        <EcomLine w="80%" data-id="element-8850" data-mp="ou218zj8o756" />
                        <EcomLine w="50%" data-id="element-8851" data-mp="ot0evywrvy85" />
                      </div>)}
                  </div>)}
              </div>
            </EcomBehindWindow>
          </motion.div>

          <div className="relative z-10 w-[340px] md:w-[400px] rounded-2xl bg-white border border-black/[0.06] shadow-[0_40px_90px_-30px_rgba(43,33,26,0.55)] overflow-hidden" data-id="element-8852" data-mp="xugpa6nl80cd">
            <div className="flex items-center gap-2 px-3 pt-3 pb-0 bg-[#EDE9E3]" data-id="element-8853" data-mp="56cfb8xzh13v">
              <span className="w-3 h-3 rounded-full bg-[#FF5F57]" data-id="element-8854" data-mp="1592ywf59n7e" />
              <span className="w-3 h-3 rounded-full bg-[#FEBC2E]" data-id="element-8855" data-mp="173l62z1717s" />
              <span className="w-3 h-3 rounded-full bg-[#28C840]" data-id="element-8856" data-mp="exp6rzfvdp9d" />
            </div>
            <div className="flex items-end gap-px px-2 pt-2 bg-[#EDE9E3] overflow-hidden" data-id="element-8857" data-mp="a5p1rsky065x">
              {ECOM_TABS.map(t => <div key={t.slug} className={`group/tab relative flex items-center gap-1 min-w-0 shrink rounded-t-lg pl-1.5 pr-1 py-1.5 ${t.active ? 'bg-white shadow-[0_-1px_3px_rgba(0,0,0,0.04)]' : 'bg-[#E2DDD5]'}`} style={{
              flexBasis: t.active ? '78px' : '34px'
            }} data-id="element-8858" data-mp="3ikp8exl0qc0m">
                  <img src={`https://cdn.simpleicons.org/${t.slug}`} alt="" aria-hidden="true" className="w-3 h-3 object-contain shrink-0" loading="lazy" onError={e => {
                const img = e.currentTarget as HTMLImageElement;
                if (!img.dataset.fallback) {
                  img.dataset.fallback = '1';
                  img.src = `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${t.slug}.svg`;
                } else {
                  img.style.visibility = 'hidden';
                }
              }} data-id="element-8859" data-mp="26vom39hnn8ii" />

                  {t.active && <span className="text-[9px] font-semibold text-neutral-700 truncate" data-id="element-8860" data-mp="2lmnpm63mv6v8">
                      {t.title}
                    </span>}
                  {t.active && <X size={9} className="shrink-0 text-neutral-400" aria-hidden="true" data-id="element-8861" data-mp="1a6fli08jznhg" />}
                </div>)}
              <span className="shrink-0 ml-0.5 mb-1 flex items-center justify-center w-4 h-4 text-neutral-400" data-id="element-8862" data-mp="1559o3p3y7c1l">
                <Plus size={11} aria-hidden="true" data-id="element-8863" data-mp="3grtp7lao9kfw" />
              </span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-white border-b border-black/[0.05]" data-id="element-8864" data-mp="3lt98b7tahgvn">
              <div className="flex-1 flex items-center gap-1.5 h-6 rounded-full bg-[#F4F0EA] px-2.5" data-id="element-8865" data-mp="3ptb9lfv448ae">
                <span className="text-[10px] font-medium text-neutral-500 truncate" data-id="element-8866" data-mp="3unfk293l6yg8">
                  admin.shopify.com/store/acme/orders
                </span>
              </div>
            </div>
            <div className="bg-white px-2 py-2" data-id="element-8867" data-mp="8g7lpqr33rtp">
              <div className="flex items-center justify-between px-2 pb-2" data-id="element-8868" data-mp="dcxyo57le7aw">
                <span className="text-[11px] font-bold text-neutral-800" data-id="element-8869" data-mp="hej8fhoty5gv">
                  Recent Orders
                </span>
                <span className="text-[9px] font-bold text-white bg-[#EA580C] rounded-full px-1.5 py-0.5" data-id="element-8870" data-mp="2f5m6ysz3fkxj">
                  12 unfulfilled
                </span>
              </div>
              <div className="divide-y divide-neutral-100" data-id="element-8871" data-mp="3mgjxug3qs41x">
                {[{
                from: '#1042',
                w: '82%',
                unread: true
              }, {
                from: '#1041',
                w: '64%',
                unread: true
              }, {
                from: '#1040',
                w: '74%',
                unread: true
              }, {
                from: '#1039',
                w: '58%',
                unread: false
              }, {
                from: '#1038',
                w: '70%',
                unread: false
              }].map((row, i) => <div key={i} className="flex items-center gap-2.5 px-2 py-1.5" data-id="element-8872" data-mp="j0mkjyaaafzd" data-mpi={i}>
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${row.unread ? 'bg-[#EA580C]' : 'bg-transparent'}`} data-id="element-8873" data-mp="e1idbd6x6sbo" />

                    <span className={`text-[10px] w-12 shrink-0 truncate ${row.unread ? 'font-bold text-neutral-800' : 'font-medium text-neutral-500'}`} data-id="element-8874" data-mp="s22w49cu4o56">
                      {row.from}
                    </span>
                    <div className="flex-1" data-id="element-8875" data-mp="16p1hpnp0gobz">
                      <EcomLine w={row.w} dark={row.unread} data-id="element-8876" data-mp="1bmt9mhpnoag6" />
                    </div>
                    <span className="text-[9px] text-neutral-400 shrink-0" data-id="element-8877" data-mp="1bkgxil6kwub1">
                      {i + 1}m
                    </span>
                  </div>)}
              </div>
            </div>
          </div>

          <motion.div className="absolute bottom-4 right-2 md:right-6 z-20 flex items-center gap-2 rounded-full bg-[#2B211A] pl-2 pr-3 py-1.5 shadow-[0_16px_30px_-12px_rgba(43,33,26,0.6)]" animate={reduced ? undefined : {
          y: [0, -6, 0]
        }} transition={reduced ? undefined : {
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-8878" data-mp="nx8r1zxezf7f">
            <span className="flex -space-x-1.5" data-id="element-8879" data-mp="iw2tnost73rk">
              {['#EA580C', '#3B82F6', '#8B5CF6'].map(c => <span key={c} className="w-3.5 h-3.5 rounded-full border border-[#2B211A]" style={{
              backgroundColor: c
            }} data-id="element-8880" data-mp="50hahy99be72" />)}
            </span>
            <span className="text-[11px] font-bold text-white" data-id="element-8881" data-mp="1vgl7eq0jf15">
              11 tabs open
            </span>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}
// --- CUSTOM E-COMMERCE STEPS ---
function EcommerceSteps() {
  const ctaRef = useRef<HTMLDivElement>(null);
  const ctaInView = useInView(ctaRef, {
    margin: '0px 0px -10% 0px'
  });
  useSuppressFloatingCta(ctaInView);
  return <section className="w-full bg-[#F4F0EA] py-24 md:py-32 overflow-hidden border-y border-neutral-200/70" data-id="element-8882" data-mp="3fd96n7vqfvh9">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-8883" data-mp="3aba05p4p4ig3">
        <div className="text-center max-w-3xl mx-auto mb-14 md:mb-16" data-id="element-8884" data-mp="35cqfds1u9cpb">
          <h2 className="text-3xl md:text-5xl font-title font-medium text-[#2B211A] tracking-tight leading-[1.1] mb-5 md:mb-6" data-id="element-8885" data-mp="31bwsn204u3y0">
            Up and running in <br className="md:hidden" data-id="element-8886" data-mp="13ku5snz8ecx6" />
            <span className="whitespace-nowrap" data-id="element-8887" data-mp="13jsr89j0b8ap">2 minutes</span>
          </h2>
          <p className="text-base md:text-lg text-neutral-500 font-medium leading-relaxed max-w-2xl mx-auto" data-id="element-8888" data-mp="zlbglg0dnfoj">
            No setup project. No IT ticket. <br className="md:hidden" data-id="element-8889" data-mp="2mgq68s2hqdxx" />
            Connect your store and go.
          </p>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="rounded-2xl md:rounded-[2rem] border border-neutral-200/70 bg-white/40 grid grid-cols-1 md:grid-cols-3 mb-14 md:mb-16 overflow-hidden" data-id="element-8890" data-mp="2r7gdkauh8qv9">
          {[{
          n: '01',
          title: 'Connect',
          body: 'Add Giga to Slack, then link Shopify, Stripe, and Gorgias with one click.'
        }, {
          n: '02',
          title: 'Ask',
          body: 'Ask any question in plain English: "Why did conversion drop yesterday?"'
        }, {
          n: '03',
          title: 'Understand',
          body: 'Giga retrieves context across your stack and gives you the complete answer.'
        }].map((step, i) => <div key={step.n} className={`flex flex-col p-8 md:p-10 ${i < 2 ? 'border-b md:border-b-0 md:border-r border-neutral-200/70' : ''}`} data-id="element-8891" data-mp="1nxu3swxxa4h4" data-mpi={i}>
              <div className="flex-1 mb-8" data-id="element-8892" data-mp="1xt46zo024hwh">
                <div className="flex items-center gap-3 mb-6" data-id="element-8893" data-mp="1xs2stanqvfaj">
                  <span className="font-mono text-sm font-bold tracking-[0.2em] text-[#EA580C]" data-id="element-8894" data-mp="1swdrp2w2tqc4">
                    {step.n}
                  </span>
                  <span className="h-px w-7 bg-[#EA580C]" data-id="element-8895" data-mp="106ez4xiobb2p" />
                </div>
                <h3 className="text-2xl font-title font-medium text-[#2B211A] tracking-tight leading-tight mb-4" data-id="element-8896" data-mp="104bzrmvms5lb">
                  {step.title}
                </h3>
                <p className="text-neutral-500 leading-relaxed text-sm md:text-base font-medium max-w-xs" data-id="element-8897" data-mp="1a3t2pwji05dl">
                  {step.body}
                </p>
              </div>
            </div>)}
        </motion.div>

        <div ref={ctaRef} className="flex flex-col items-center gap-4" data-id="element-8898" data-mp="hfdze79qbbi3">
          <WaitlistCTA className="group inline-flex items-center justify-center gap-2.5 rounded-full bg-[#EA580C] px-8 py-4 text-base font-bold text-white shadow-lg shadow-[#EA580C]/20 transition-all duration-300 hover:bg-[#d24e09] hover:scale-105" data-id="element-8899" data-mp="2t27stxoz0359">
            Get started for free
            <ArrowRight size={18} strokeWidth={2.5} className="transition-transform duration-300 group-hover:translate-x-0.5" data-id="element-8900" data-mp="2o3o5ptyn4lbb" />
          </WaitlistCTA>
        </div>
      </div>
    </section>;
}
// --- CUSTOM E-COMMERCE SCENARIO ---
function EcommerceScenario() {
  return <section className="w-full bg-[#FAF8F4] py-24 md:py-32 border-t border-neutral-200/70 overflow-hidden" data-id="element-8901" data-mp="3gqh9ymb8o2q2">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-8902" data-mp="37s5aduse9ulh">
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16" data-id="element-8903" data-mp="37qltq32tthj6">
          <h2 className="text-3xl md:text-5xl font-title font-medium text-[#2B211A] tracking-tight leading-[1.1] mb-5 md:mb-6" data-id="element-8904" data-mp="1rdngjtud02th">
            The employee who already knows everything
          </h2>
          <p className="text-base md:text-lg text-neutral-500 font-medium leading-relaxed max-w-2xl mx-auto" data-id="element-8905" data-mp="2n3br017t8g9k">
            Giga has read every Slack message, support ticket, order, and
            meeting note. Ask a question, and it reasons across your entire
            stack to give you the complete picture.
          </p>
        </div>

        <div className="max-w-4xl mx-auto bg-white rounded-2xl border border-neutral-200/70 shadow-xl overflow-hidden" data-id="element-8906" data-mp="29551p9gxfoli">
          <div className="bg-[#3F0E40] px-4 py-3 flex items-center gap-2" data-id="element-8907" data-mp="36svr8n9u0g5z">
            <span className="w-3 h-3 rounded-full bg-[#FF5F57]" data-id="element-8908" data-mp="31srffajvc9m3" />
            <span className="w-3 h-3 rounded-full bg-[#FEBC2E]" data-id="element-8909" data-mp="2x07shmzak24m" />
            <span className="w-3 h-3 rounded-full bg-[#28C840]" data-id="element-8910" data-mp="2s2g1cv6h0k1h" />
            <span className="ml-4 text-white font-bold text-sm" data-id="element-8911" data-mp="2s1wk7gon9zoi">
              # cx-escalations
            </span>
          </div>
          <div className="p-6 md:p-10 space-y-8 bg-[#F8F9FA]" data-id="element-8912" data-mp="3kqtxf5e016ae">
            {/* User Message */}
            <div className="flex items-start gap-4" data-id="element-8913" data-mp="3s2026teuryyq">
              <img src="https://i.pravatar.cc/150?img=32" alt="User" className="w-10 h-10 rounded-lg" data-id="element-8914" data-mp="3s3jhagorw3yx" />

              <div data-id="element-8915" data-mp="w77wnu4sutk">
                <div className="flex items-baseline gap-2" data-id="element-8916" data-mp="xrvtulxxfig">
                  <span className="font-bold text-neutral-900" data-id="element-8917" data-mp="5tz0zl6iimzc">
                    Alex (Head of CX)
                  </span>
                  <span className="text-xs text-neutral-500" data-id="element-8918" data-mp="av66tep3hc3d">10:14 AM</span>
                </div>
                <p className="mt-1 text-neutral-800" data-id="element-8919" data-mp="ev6or9bek5f6">
                  <span className="text-[#1264A3] bg-[#1264A3]/10 px-1 rounded font-semibold" data-id="element-8920" data-mp="jtrpd9ukgr45">
                    @Giga
                  </span>{' '}
                  Why are refunds increasing this week?
                </p>
              </div>
            </div>

            {/* Giga Reply */}
            <div className="flex items-start gap-4" data-id="element-8921" data-mp="yjz69l4xe22q">
              <div className="w-10 h-10 rounded-lg bg-[#EA580C] flex items-center justify-center shrink-0" data-id="element-8922" data-mp="yhcrb0fpgkah">
                <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="Giga" className="w-6 h-6 object-contain [filter:brightness(0)_invert(1)]" data-id="element-8923" data-mp="12ioivbi53flb" />
              </div>
              <div className="flex-1" data-id="element-8924" data-mp="2fvjzogw47rfs">
                <div className="flex items-baseline gap-2" data-id="element-8925" data-mp="2ku3mskmg399q">
                  <span className="font-bold text-neutral-900" data-id="element-8926" data-mp="2ktllh9kxe4uy">Giga</span>
                  <span className="text-[10px] font-bold text-white bg-neutral-400 px-1.5 py-0.5 rounded uppercase" data-id="element-8927" data-mp="1o4w2ffxkb67e">
                    App
                  </span>
                  <span className="text-xs text-neutral-500" data-id="element-8928" data-mp="1x4ivneg515pd">10:15 AM</span>
                </div>
                <p className="mt-1 text-neutral-800 mb-4" data-id="element-8929" data-mp="223lz4uggz3v8">
                  Refunds are up 14% this week. I checked Gorgias and found 32
                  tickets mentioning "sizing" for the new Summer Collection. I
                  also checked Shopify and saw that 80% of these returns are for
                  the "Classic Crew" in Medium. I've drafted a Slack message to
                  the product team with these details.
                </p>

                <div className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3 shadow-sm" data-id="element-8930" data-mp="wwuijutyab4a">
                  <div className="flex items-center gap-3" data-id="element-8931" data-mp="s419wvvd2imx">
                    <img src="https://cdn.simpleicons.org/gorgias" alt="Gorgias" className="w-4 h-4" data-id="element-8932" data-mp="n7ay4kqla97b" />

                    <span className="text-sm text-neutral-700" data-id="element-8933" data-mp="j57jjn8mioht">
                      Analyzed 150+ recent tickets in Gorgias.
                    </span>
                    <Check size={16} className="text-green-600 ml-auto" data-id="element-8934" data-mp="e87e9ejllprr" />
                  </div>
                  <div className="flex items-center gap-3" data-id="element-8935" data-mp="98uj86ikpce3">
                    <img src="https://cdn.simpleicons.org/shopify" alt="Shopify" className="w-4 h-4" data-id="element-8936" data-mp="48ynsvnxyta5" />

                    <span className="text-sm text-neutral-700" data-id="element-8937" data-mp="23jhoo8nft0iv">
                      Correlated returns with Shopify order data.
                    </span>
                    <Check size={16} className="text-green-600 ml-auto" data-id="element-8938" data-mp="3t26fhi6mfyi2" />
                  </div>
                  <div className="flex items-center gap-3" data-id="element-8939" data-mp="1myzy6zpem3n">
                    <img src="https://cdn.simpleicons.org/slack" alt="Slack" className="w-4 h-4" data-id="element-8940" data-mp="1oju3vi3w30z" />

                    <span className="text-sm text-neutral-700" data-id="element-8941" data-mp="3j5jtur6h13ez">
                      Drafted summary for the #product channel.
                    </span>
                    <Check size={16} className="text-green-600 ml-auto" data-id="element-8942" data-mp="fmqkyron12r5" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
// --- CUSTOM E-COMMERCE BENEFITS SECTION ---
function EcomWorksWhereYouWorkVisual() {
  const reduced = !!useReducedMotion();
  const [step, setStep] = useState(reduced ? 3 : 0);
  const FAITH_MSG = 'why are sales down today? Check Shopify and Meta Ads';
  const MARCUS_TYPED = 'and pull the Gorgias tickets too \uD83D\uDE4F';
  const [typedLen, setTypedLen] = useState(reduced ? MARCUS_TYPED.length : 0);
  useEffect(() => {
    if (reduced) return;
    let timers: ReturnType<typeof setTimeout>[] = [];
    let typeInterval: ReturnType<typeof setInterval> | undefined;
    const run = () => {
      setStep(0);
      setTypedLen(0);
      let i = 0;
      typeInterval = setInterval(() => {
        i += 1;
        setTypedLen(i);
        if (i >= MARCUS_TYPED.length) {
          clearInterval(typeInterval);
          timers.push(setTimeout(() => setStep(1), 450));
          timers.push(setTimeout(() => setStep(2), 1250));
          timers.push(setTimeout(() => setStep(3), 2050));
          timers.push(setTimeout(run, 7200));
        }
      }, 30);
    };
    timers.push(setTimeout(run, 600));
    return () => {
      timers.forEach(clearTimeout);
      if (typeInterval) clearInterval(typeInterval);
    };
  }, [reduced]);
  const marcusSent = step >= 1;
  const gigaTyping = step === 2;
  const gigaReplied = step >= 3;
  return <div className="absolute inset-0 overflow-hidden" data-id="element-8943" data-mp="1hcgpo7v6kq8k">
      <div className="absolute top-4 md:top-6 left-1/2 origin-top -translate-x-1/2 scale-[0.58] sm:scale-[0.66] md:scale-[0.82] lg:scale-[0.9]" data-id="element-8944" data-mp="knz65vgbwlz6">
        <div className="relative h-[800px] w-[390px] rounded-[3.6rem] p-[4px] shadow-[0_18px_50px_-16px_rgba(43,33,26,0.55)]" style={{
        background: 'linear-gradient(150deg, #5A5A5E 0%, #232326 28%, #050505 60%, #34343A 100%)'
      }} data-id="element-8945" data-mp="omos4cffnbuf">
          <div className="relative h-full w-full rounded-[3.05rem] bg-black p-[10px]" data-id="element-8946" data-mp="13e7664oqewb5">
            <div className="relative h-full w-full rounded-[2.5rem] bg-[#3F0E40] overflow-hidden flex flex-col" data-id="element-8947" data-mp="18cifmeb474su">
              <div className="relative bg-[#3F0E40] px-6 pt-3 pb-1.5 flex items-center justify-between shrink-0" data-id="element-8948" data-mp="1ce3w09muy17s">
                <span className="text-[13px] font-semibold text-white tracking-tight" data-id="element-8949" data-mp="1hcdpmfsxotra">
                  9:15
                </span>
                <span aria-hidden="true" className="absolute left-1/2 top-2 -translate-x-1/2 h-6 w-[88px] rounded-full bg-black" data-id="element-8950" data-mp="3e7tgx6b0w9e9" />

                <span className="flex items-center gap-1.5 text-white" aria-hidden="true" data-id="element-8951" data-mp="3bl0vo79pup2a">
                  <span className="flex items-end gap-0.5 h-3" data-id="element-8952" data-mp="36ukdk9uyaf9l">
                    <span className="w-0.5 h-1.5 rounded-sm bg-white" data-id="element-8953" data-mp="36t9e2m8jftoa" />
                    <span className="w-0.5 h-2 rounded-sm bg-white" data-id="element-8954" data-mp="31wiuvq1fshyn" />
                    <span className="w-0.5 h-2.5 rounded-sm bg-white" data-id="element-8955" data-mp="2wwo3lldr1qd2" />
                    <span className="w-0.5 h-3 rounded-sm bg-white" data-id="element-8956" data-mp="2wujsaiditp2r" />
                  </span>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" data-id="element-8957" data-mp="3ui2a0ip5u1iw">
                    <path d="M5 12.55a11 11 0 0 1 14.08 0" data-id="element-8958" data-mp="3kknuqmez3is6"></path>
                    <path d="M1.42 9a16 16 0 0 1 21.16 0" data-id="element-8959" data-mp="3gkwyi36ogag9"></path>
                    <path d="M8.53 16.11a6 6 0 0 1 6.95 0" data-id="element-8960" data-mp="3gmgertsc2li1"></path>
                    <line x1="12" y1="20" x2="12.01" y2="20" data-id="element-8961" data-mp="297le5rcvit80"></line>
                  </svg>
                  <span className="relative ml-0.5 flex items-center" data-id="element-8962" data-mp="2545ioycjiyww">
                    <span className="h-3 w-6 rounded-[3px] border border-white/80 p-[1.5px] flex" data-id="element-8963" data-mp="26mniijxs9p6g">
                      <span className="h-full w-[80%] rounded-[1px] bg-white" data-id="element-8964" data-mp="2bm0dxt2q04kn" />
                    </span>
                    <span className="ml-px h-1.5 w-0.5 rounded-r-sm bg-white/80" data-id="element-8965" data-mp="1woptvvc0hqdb" />
                  </span>
                </span>
              </div>
              <div className="bg-[#3F0E40] px-4 pt-1.5 pb-3 flex items-center gap-2.5 shrink-0" data-id="element-8966" data-mp="21lg9yintglyq">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-white shrink-0" data-id="element-8967" data-mp="1nul8hrfnd756">
                  <SlackMark className="w-5 h-5" data-id="element-8968" data-mp="1sopeo8h39b57" />
                </span>
                <div className="min-w-0 flex-1" data-id="element-8969" data-mp="1dz1b10lfq0eb">
                  <span className="block text-base font-bold text-white leading-tight truncate" data-id="element-8970" data-mp="1iwt623hdm2mq">
                    Acme Store
                  </span>
                  <span className="block text-xs text-white/55 leading-tight" data-id="element-8971" data-mp="14xk0ryupksxj">
                    Slack
                  </span>
                </div>
                <SlackMark className="w-5 h-5 opacity-80 shrink-0" data-id="element-8972" data-mp="qa1rsoa45c5b" />
              </div>
              <div className="bg-[#3F0E40] border-t border-white/10 px-4 pb-3 flex items-center gap-2 shrink-0" data-id="element-8973" data-mp="lb8dwuqwzj4m">
                <HashIcon size={16} className="text-white/70 shrink-0" data-id="element-8974" data-mp="zarnlrzdj2j1" />
                <span className="text-sm font-bold text-white truncate" data-id="element-8975" data-mp="z8n78akagb1v">
                  ops
                </span>
                <span className="ml-auto flex items-center gap-1.5 text-xs font-semibold text-white/55 shrink-0" data-id="element-8976" data-mp="198xllyi27oim">
                  <span className="inline-block w-2 h-2 rounded-full bg-[#2EB67D]" data-id="element-8977" data-mp="14amcjpzl9i1g" />
                  12
                </span>
              </div>

              <div className="flex-1 px-4 pt-3 pb-4 flex flex-col justify-start bg-white overflow-hidden" data-id="element-8978" data-mp="1i0pqltobe5ft">
                <motion.div animate={{
                y: reduced ? 0 : gigaReplied ? -128 : gigaTyping ? -46 : marcusSent ? -20 : 0
              }} transition={{
                type: 'spring',
                stiffness: 180,
                damping: 26
              }} data-id="element-8979" data-mp="1e20gc9ykp40e">
                  <div className="flex flex-col gap-4" data-id="element-8980" data-mp="2uci14q1hzo4y">
                    <div className="flex items-start gap-3" data-id="element-8981" data-mp="2yed3zyz2u9ku">
                      <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/ChatGPT_Image_Apr_24,_2026,_12_02_55_PM_3.webp" alt="Faith" className="w-11 h-11 rounded-lg object-cover shrink-0" loading="lazy" data-id="element-8982" data-mp="2ygps6tvfhg6c" />

                      <div className="min-w-0" data-id="element-8983" data-mp="2aqvde6rjthjg">
                        <div className="flex items-baseline gap-2" data-id="element-8984" data-mp="2fnlsaqrma73a">
                          <span className="text-base font-bold text-neutral-900 leading-none" data-id="element-8985" data-mp="2flsqppvv2d25">
                            Faith
                          </span>
                          <span className="text-sm text-neutral-400 leading-none" data-id="element-8986" data-mp="2pfj9ce5b3t9x">
                            9:14
                          </span>
                        </div>
                        <p className="mt-2 text-base text-neutral-700 leading-snug" data-id="element-8987" data-mp="1wwm07gotsh5s">
                          <span className="font-semibold text-[#1264A3] bg-[#1264A3]/10 rounded px-1 py-0.5" data-id="element-8988" data-mp="1peo3kcrqt0jz">
                            @Giga
                          </span>{' '}
                          {FAITH_MSG}
                        </p>
                      </div>
                    </div>

                    <AnimatePresence data-id="element-8989" data-mp="1ahdoyujszek1">
                      {marcusSent && <motion.div className="flex items-start gap-3" initial={reduced ? false : {
                      opacity: 0,
                      y: 10,
                      scale: 0.96
                    }} animate={{
                      opacity: 1,
                      y: 0,
                      scale: 1
                    }} transition={{
                      type: 'spring',
                      stiffness: 320,
                      damping: 22
                    }} data-id="element-8990" data-mp="1aiyeu6v6a9bk">
                          <img src="https://randomuser.me/api/portraits/men/32.jpg" alt="Marcus" className="w-11 h-11 rounded-lg object-cover shrink-0" loading="lazy" data-id="element-8991" data-mp="mtdvvnz0rc2a" />

                          <div className="min-w-0" data-id="element-8992" data-mp="2eudpu5wb29r0">
                            <div className="flex items-baseline gap-2" data-id="element-8993" data-mp="29wkl7hanhnsu">
                              <span className="text-base font-bold text-neutral-900 leading-none" data-id="element-8994" data-mp="29yonjuswier0">
                                Marcus
                              </span>
                              <span className="text-sm text-neutral-400 leading-none" data-id="element-8995" data-mp="2jw337s702zi9">
                                9:15
                              </span>
                            </div>
                            <p className="mt-2 text-base text-neutral-700 leading-snug" data-id="element-8996" data-mp="1r7dyzkanee2a">
                              {MARCUS_TYPED}
                            </p>
                          </div>
                        </motion.div>}
                    </AnimatePresence>

                    <AnimatePresence data-id="element-8997" data-mp="1cgoko91w4iu7">
                      {gigaTyping && <motion.div className="flex items-center gap-2" initial={reduced ? false : {
                      opacity: 0,
                      y: 6
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} exit={{
                      opacity: 0
                    }} transition={{
                      duration: 0.25
                    }} data-id="element-8998" data-mp="1cfdkskbkfv8d">
                          <span className="w-11 h-11 rounded-lg bg-[#EA580C] flex items-center justify-center shrink-0" data-id="element-8999" data-mp="obnqavpj3s2p">
                            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="Giga" className="w-6 h-6 object-contain [filter:brightness(0)_invert(1)]" data-id="element-9000" data-mp="1h1mhoxr741aj" />
                          </span>
                          <span className="flex items-center gap-1.5 rounded-full bg-neutral-100 px-3.5 py-2.5" data-id="element-9001" data-mp="1c2t3f343469b">
                            {[0, 1, 2].map(d => <motion.span key={d} className="w-2.5 h-2.5 rounded-full bg-neutral-400" animate={{
                          opacity: [0.3, 1, 0.3]
                        }} transition={{
                          duration: 0.9,
                          repeat: Infinity,
                          delay: d * 0.18
                        }} data-id="element-9002" data-mp="3rtfmkmap6y6p" />)}
                          </span>
                        </motion.div>}
                    </AnimatePresence>

                    <AnimatePresence data-id="element-9003" data-mp="1olyjx4gl4ci5">
                      {gigaReplied && <motion.div className="flex items-start gap-3" initial={reduced ? false : {
                      opacity: 0,
                      y: 10,
                      scale: 0.96
                    }} animate={{
                      opacity: 1,
                      y: 0,
                      scale: 1
                    }} transition={{
                      type: 'spring',
                      stiffness: 320,
                      damping: 22
                    }} data-id="element-9004" data-mp="1olez9fzjva0f">
                          <span className="w-11 h-11 rounded-lg bg-[#EA580C] flex items-center justify-center shrink-0" data-id="element-9005" data-mp="34z5g7nbrpsrh">
                            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="Giga" className="w-6 h-6 object-contain [filter:brightness(0)_invert(1)]" data-id="element-9006" data-mp="2zz0yxv31cu07" />
                          </span>
                          <div className="min-w-0" data-id="element-9007" data-mp="3hcil93o3ad2b">
                            <div className="flex items-baseline gap-2" data-id="element-9008" data-mp="3m87pvleyuk5h">
                              <span className="text-base font-bold text-neutral-900 leading-none" data-id="element-9009" data-mp="2olqw2q5fyooh">
                                Giga
                              </span>
                              <span className="text-[11px] font-bold text-[#616061] bg-neutral-200/80 rounded px-1.5 py-0.5 leading-none uppercase tracking-wide" data-id="element-9010" data-mp="2tmetpuo6e3ro">
                                App
                              </span>
                            </div>
                            <p className="mt-2 text-base text-neutral-700 leading-snug" data-id="element-9011" data-mp="dsxpqiumzp0c">
                              On it. Conversions are down 12% today. Checked
                              Meta Ads — the new campaign is underperforming.
                              Checked Gorgias — 15 tickets about a broken
                              discount code. 🎉
                            </p>
                            <motion.div className="mt-2.5 rounded-xl bg-[#FBEDE6] px-3 py-3" initial={reduced ? false : {
                          opacity: 0,
                          y: 8
                        }} animate={{
                          opacity: 1,
                          y: 0
                        }} transition={reduced ? {
                          duration: 0.2
                        } : {
                          delay: 0.45,
                          duration: 0.55,
                          ease: [0.22, 1, 0.36, 1]
                        }} data-id="element-9012" data-mp="qdmlq8kyy29w">
                              <div className="flex items-center gap-2" data-id="element-9013" data-mp="1j1s8f0vl21cd">
                                <CheckIcon size={18} strokeWidth={2.5} className="text-[#C2410C] shrink-0" data-id="element-9014" data-mp="1e1w3mzf2a5vr" />

                                <div className="min-w-0" data-id="element-9015" data-mp="1xmwczbzlytkd">
                                  <span className="block text-sm font-semibold text-neutral-900 leading-tight" data-id="element-9016" data-mp="1xlbdd7yfskfn">
                                    Analysis complete
                                  </span>
                                  <span className="block text-xs text-neutral-500 leading-tight" data-id="element-9017" data-mp="1nrm5w9ucrvzq">
                                    Checked Shopify, Meta, and Gorgias
                                  </span>
                                </div>
                              </div>
                              <div className="mt-2.5 border-t border-[#C2410C]/15 pt-2.5 flex flex-col gap-1.5" data-id="element-9018" data-mp="1irgemurzwbi6">
                                {['Shopify orders analyzed', 'Meta Ads ROAS checked', 'Gorgias tickets scanned', 'Summary posted to #leadership'].map((item, i) => <motion.div key={item} className="flex items-center gap-1.5 text-xs font-semibold text-[#0F9D58]" initial={reduced ? false : {
                              opacity: 0,
                              x: -4
                            }} animate={{
                              opacity: 1,
                              x: 0
                            }} transition={reduced ? {
                              duration: 0.2
                            } : {
                              delay: 0.7 + i * 0.18,
                              duration: 0.3
                            }} data-id="element-9019" data-mp="v02kcjaaa5uk" data-mpi={i}>
                                    <CheckIcon size={14} strokeWidth={3} className="shrink-0" data-id="element-9020" data-mp="zsvc7u6f054n" />

                                    <span className="text-neutral-700 font-medium" data-id="element-9021" data-mp="uvv6jkdh94e2">
                                      {item}
                                    </span>
                                  </motion.div>)}
                              </div>
                            </motion.div>
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>
                </motion.div>
              </div>

              <div className="px-4 pb-1.5 shrink-0" data-id="element-9022" data-mp="1l53yrir8dly3">
                <div className="rounded-xl border border-neutral-200 bg-white px-3 pt-2.5 pb-2" data-id="element-9023" data-mp="1l6f43n0br1rb">
                  <p className="text-sm leading-snug min-h-[20px] text-neutral-700" data-id="element-9024" data-mp="1q4givvqpwewz">
                    {!marcusSent ? <>
                        <span data-id="element-9025" data-mp="1v16y6gup76hc">{MARCUS_TYPED.slice(0, typedLen)}</span>
                        {!reduced && <motion.span aria-hidden="true" className="inline-block w-px h-4 -mb-0.5 ml-px bg-[#EA580C] align-middle" animate={{
                      opacity: [1, 1, 0, 0]
                    }} transition={{
                      duration: 1,
                      repeat: Infinity,
                      ease: 'linear'
                    }} data-id="element-9026" data-mp="1zv1k7qxrzxjl" />}
                      </> : <span className="text-neutral-300" data-id="element-9027" data-mp="2irjikr74qvot">Message #ops</span>}
                  </p>
                  <div className="mt-2 flex items-center justify-between" data-id="element-9028" data-mp="jgid9uatj3x3">
                    <span className="flex items-center gap-3 text-neutral-300" data-id="element-9029" data-mp="oebkmqnuy3yy">
                      <PlusIcon size={18} strokeWidth={2.5} data-id="element-9030" data-mp="2qc3u6ftfnu2v" />
                      <SmileIcon size={18} strokeWidth={2.5} data-id="element-9031" data-mp="2qaiy2lrh032w" />
                    </span>
                    <motion.span className={`flex items-center justify-center w-8 h-8 rounded-lg ${typedLen > 0 && !marcusSent ? 'bg-[#007A5A]' : 'bg-neutral-200'}`} animate={reduced || marcusSent || typedLen === 0 ? undefined : {
                    scale: [1, 1.12, 1]
                  }} transition={reduced ? undefined : {
                    duration: 1.4,
                    repeat: Infinity,
                    ease: 'easeInOut'
                  }} data-id="element-9032" data-mp="2lc9248xx8yg8">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={typedLen > 0 && !marcusSent ? 'text-white' : 'text-neutral-400'} data-id="element-9033" data-mp="2hb5q5xizvmka">
                        <line x1="22" y1="2" x2="11" y2="13" data-id="element-9034" data-mp="3sp11swaoxd6r"></line>
                        <polygon points="22 2 15 22 11 13 2 9 22 2" data-id="element-9035" data-mp="3nsanadejapng"></polygon>
                      </svg>
                    </motion.span>
                  </div>
                </div>
              </div>

              <div className="pb-3 pt-1.5 flex justify-center bg-white shrink-0" data-id="element-9036" data-mp="44z9wz40q2la">
                <span className="h-1.5 w-32 rounded-full bg-neutral-900/80" data-id="element-9037" data-mp="3malu7ss92ow1" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
const ECOM_CONNECT_TILES = [{
  slug: 'shopify',
  name: 'Shopify',
  size: 56,
  top: '6%',
  left: '8%',
  rotate: -6,
  delay: 0.2
}, {
  slug: 'stripe',
  name: 'Stripe',
  size: 50,
  top: '2%',
  left: '40%',
  rotate: -4,
  delay: 0
}, {
  slug: 'klaviyo',
  name: 'Klaviyo',
  size: 46,
  top: '8%',
  left: '74%',
  rotate: 6,
  delay: 0.5
}, {
  slug: 'gorgias',
  name: 'Gorgias',
  size: 44,
  top: '34%',
  left: '24%',
  rotate: 4,
  delay: 0.9
}, {
  slug: 'meta',
  name: 'Meta Ads',
  size: 52,
  top: '38%',
  left: '54%',
  rotate: -5,
  delay: 1.2
}, {
  slug: 'googleads',
  name: 'Google Ads',
  size: 44,
  top: '32%',
  left: '84%',
  rotate: 5,
  delay: 0.7
}, {
  slug: 'googlesheets',
  name: 'Google Sheets',
  size: 50,
  top: '64%',
  left: '6%',
  rotate: 6,
  delay: 0.3
}, {
  slug: 'slack',
  name: 'Slack',
  size: 46,
  top: '68%',
  left: '36%',
  rotate: -4,
  delay: 1.4
}, {
  slug: 'notion',
  name: 'Notion',
  size: 44,
  top: '62%',
  left: '64%',
  rotate: 4,
  delay: 1.0
}, {
  slug: 'zendesk',
  name: 'Zendesk',
  size: 42,
  top: '46%',
  left: '40%',
  rotate: -5,
  delay: 0.6
}];
function EcomConnectTile({
  tile,
  reduced
}: {
  tile: (typeof ECOM_CONNECT_TILES)[number];
  reduced: boolean;
}) {
  const radius = tile.size >= 60 ? 18 : tile.size >= 50 ? 15 : 12;
  return <motion.div className="absolute bg-white shadow-[0_16px_34px_-14px_rgba(43,33,26,0.30)] ring-1 ring-black/[0.04] flex items-center justify-center" style={{
    top: tile.top,
    left: tile.left,
    width: tile.size,
    height: tile.size,
    borderRadius: radius,
    rotate: `${tile.rotate}deg`
  }} initial={{
    opacity: 0,
    scale: 0.6
  }} whileInView={{
    opacity: 1,
    scale: 1
  }} viewport={{
    once: true
  }} transition={{
    delay: tile.delay * 0.25,
    type: 'spring',
    stiffness: 220,
    damping: 17
  }} data-id="element-9038" data-mp="29h86yhh80w0g">
      <motion.img src={`https://cdn.simpleicons.org/${tile.slug}`} alt={tile.name} className="object-contain" style={{
      width: tile.size * 0.52,
      height: tile.size * 0.52
    }} loading="lazy" onError={e => {
      const el = e.currentTarget as HTMLImageElement;
      if (!el.dataset.fallback) {
        el.dataset.fallback = '1';
        el.src = `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${tile.slug}.svg`;
      } else {
        el.style.visibility = 'hidden';
      }
    }} animate={reduced ? undefined : {
      y: [0, -6, 0]
    }} transition={reduced ? undefined : {
      duration: 4 + tile.delay,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: tile.delay
    }} data-id="element-9039" data-mp="21yh72mke1819" />
    </motion.div>;
}
function EcomConnectsHub({
  reduced
}: {
  reduced: boolean;
}) {
  return <motion.div className="absolute -bottom-3 -right-3 z-10 flex items-center justify-center" initial={{
    opacity: 0,
    scale: 0.5
  }} whileInView={{
    opacity: 1,
    scale: 1
  }} viewport={{
    once: true
  }} transition={{
    delay: 0.2,
    type: 'spring',
    stiffness: 200,
    damping: 16
  }} data-id="element-9040" data-mp="2ln9pbo8bsuxs">
      <div className="relative w-[68px] h-[68px] flex items-center justify-center" data-id="element-9041" data-mp="2zmrgubd0amau">
        <span role="img" aria-label="Giga" className="w-12 h-12 bg-[#EA580C]" style={{
        WebkitMaskImage: 'url(https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png)',
        maskImage: 'url(https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png)',
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        maskPosition: 'center',
        WebkitMaskSize: 'contain',
        maskSize: 'contain'
      }} data-id="element-9042" data-mp="34k1geju40ece" />
      </div>
    </motion.div>;
}
function EcomConnectsVisual() {
  const reduced = !!useReducedMotion();
  return <div className="flex-1 relative mt-6 overflow-hidden" data-id="element-9043" data-mp="69zgepbepbm1">
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" data-id="element-9044" data-mp="18jxb2sshf6a">
        {[34, 54, 76, 98].map((r, i) => <circle key={r} cx="100" cy="104" r={r} fill="none" stroke="#EA580C" strokeOpacity={0.38 - i * 0.05} strokeWidth="0.9" strokeDasharray="1.6 2.4" vectorEffect="non-scaling-stroke" data-id="element-9045" data-mp="3shpmp1jxokwb" data-mpi={i} />)}
      </svg>
      <div className="absolute inset-0 origin-top scale-[0.82] sm:scale-100" data-id="element-9046" data-mp="k02z56b2un6q">
        {ECOM_CONNECT_TILES.map(tile => <EcomConnectTile key={tile.slug} tile={tile} reduced={reduced} data-id="element-9047" data-mp="f94g3yypfazs" />)}
      </div>
      <EcomConnectsHub reduced={reduced} data-id="element-9048" data-mp="f706d0e4jhrl" />
    </div>;
}
const ECOM_GHOST_PILLS = [{
  text: 'refunds',
  top: '6%',
  left: '40%',
  rotate: -4
}, {
  text: 'inventory',
  top: '2%',
  left: '72%',
  rotate: 5
}, {
  text: 'campaigns',
  top: '20%',
  left: '-8%',
  rotate: -7
}, {
  text: 'tickets',
  top: '24%',
  left: '74%',
  rotate: 6
}, {
  text: 'ROAS',
  top: '44%',
  left: '-6%',
  rotate: 4
}, {
  text: 'shipping',
  top: '46%',
  left: '82%',
  rotate: -5
}, {
  text: 'discounts',
  top: '74%',
  left: '14%',
  rotate: -4
}, {
  text: 'abandoned',
  top: '70%',
  left: '-10%',
  rotate: 6
}, {
  text: 'LTV',
  top: '88%',
  left: '64%',
  rotate: 5
}];
const ECOM_ACCENT_PILLS = [{
  text: 'insights',
  top: '20%',
  left: '14%',
  rotate: -6,
  bg: 'bg-[#F59E5B]',
  delay: 0
}, {
  text: 'actions',
  top: '42%',
  left: '32%',
  rotate: 5,
  bg: 'bg-[#EA580C]',
  delay: 0.12
}, {
  text: 'reports',
  top: '64%',
  left: '46%',
  rotate: -5,
  bg: 'bg-[#C2410C]',
  delay: 0.24
}];
function EcomPartnerVisual() {
  const reduced = !!useReducedMotion();
  return <div className="flex-1 relative mt-6 overflow-hidden" data-id="element-9049" data-mp="11em2fs3466s5">
      <div className="absolute inset-0 origin-top scale-[0.8] sm:scale-100" data-id="element-9050" data-mp="1p589ph5ann13">
        {ECOM_GHOST_PILLS.map((pill, i) => <motion.span key={pill.text} className="absolute whitespace-nowrap px-4 py-2 rounded-full text-sm font-bold text-neutral-300 bg-neutral-100" style={{
        top: pill.top,
        left: pill.left,
        rotate: `${pill.rotate}deg`
      }} initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: i * 0.04,
        duration: 0.4
      }} data-id="element-9051" data-mp="1txt0sawtfm0x" data-mpi={i}>
            {pill.text}
          </motion.span>)}
        {ECOM_ACCENT_PILLS.map(pill => <motion.span key={pill.text} className="absolute z-10" style={{
        top: pill.top,
        left: pill.left
      }} initial={{
        opacity: 0,
        scale: 0.7,
        y: 12
      }} whileInView={{
        opacity: 1,
        scale: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.3 + pill.delay,
        type: 'spring',
        stiffness: 240,
        damping: 18
      }} data-id="element-9052" data-mp="2vqn5sddpdl3q">
            <motion.span className={`block whitespace-nowrap px-5 py-2.5 sm:px-6 sm:py-3 rounded-full text-base sm:text-lg font-bold text-white shadow-[0_16px_34px_-12px_rgba(43,33,26,0.45)] ${pill.bg}`} style={{
          rotate: `${pill.rotate}deg`
        }} animate={reduced ? undefined : {
          y: [0, -5, 0]
        }} transition={reduced ? undefined : {
          duration: 4 + pill.delay * 4,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-9053" data-mp="fxy1ooksnq8h">
              {pill.text}
            </motion.span>
          </motion.span>)}
      </div>
    </div>;
}
const ECOM_FT_CARD_BASE = 'w-[156px] h-[176px] shrink-0 rounded-xl bg-white border border-neutral-200 shadow-[0_18px_40px_-18px_rgba(43,33,26,0.35)] overflow-hidden flex flex-col';
function EcomEmailCard() {
  return <div className={ECOM_FT_CARD_BASE} data-id="element-9054" data-mp="iyrdyntao6jj">
      <div className="flex items-center gap-1.5 px-2 py-1.5 bg-[#EA580C] shrink-0" data-id="element-9055" data-mp="izszxnbumdg1">
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-white shrink-0" data-id="element-9056" data-mp="3hnykpp2lblpt">
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" data-id="element-9057" data-mp="23t9pbca3a5s1"></path>
          <polyline points="22,6 12,13 2,6" data-id="element-9058" data-mp="1zr4ysmextp9m"></polyline>
        </svg>
        <span className="text-[8px] font-bold text-white truncate" data-id="element-9059" data-mp="1usbh0hsmbc3n">
          Email sent
        </span>
      </div>
      <div className="p-2.5 flex-1 flex flex-col" data-id="element-9060" data-mp="1ptqh6jha2ufq">
        <div className="flex items-center gap-1 text-[7px] text-neutral-400 mb-1" data-id="element-9061" data-mp="1psyxa5x1ml5a">
          <span className="font-semibold text-neutral-500" data-id="element-9062" data-mp="1kuxbhd88g7q4">To:</span>{' '}
          supplier@acme.com
        </div>
        <div className="text-[9.5px] font-bold text-neutral-900 leading-tight mb-1.5" data-id="element-9063" data-mp="1g24bf2nplnkd">
          Reorder: Classic Crew
        </div>
        <div className="space-y-1" data-id="element-9064" data-mp="172phft8c2uv4">
          <div className="h-1 w-full rounded bg-neutral-100" data-id="element-9065" data-mp="173s9i4he73cm" />
          <div className="h-1 w-[94%] rounded bg-neutral-100" data-id="element-9066" data-mp="123cyvkohw9yc" />
          <div className="h-1 w-2/3 rounded bg-neutral-100" data-id="element-9067" data-mp="122ushv84cfcp" />
        </div>
        <div className="mt-auto flex items-center gap-1" data-id="element-9068" data-mp="x5umtlf6lem4">
          <span className="flex items-center justify-center w-3 h-3 rounded-full bg-[#0F9D58] shrink-0" data-id="element-9069" data-mp="2lz0b49z6772o">
            <CheckIcon size={8} strokeWidth={4} className="text-white" data-id="element-9070" data-mp="2q02g77frqdck" />
          </span>
          <span className="text-[8px] font-semibold text-[#0F9D58]" data-id="element-9071" data-mp="2c0jeoxhgq098">
            Delivered · just now
          </span>
        </div>
      </div>
    </div>;
}
function EcomReportCard() {
  return <div className={ECOM_FT_CARD_BASE} data-id="element-9072" data-mp="2h1gzlhtux8ef">
      <div className="flex items-center gap-1.5 px-2 py-1.5 bg-[#EA580C] shrink-0" data-id="element-9073" data-mp="22ahvok3yv816">
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-white shrink-0" data-id="element-9074" data-mp="271zvh4u972kk">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" data-id="element-9075" data-mp="1y8wkd372z1t3"></path>
          <polyline points="14 2 14 8 20 8" data-id="element-9076" data-mp="1jbvr2v98qysi"></polyline>
          <line x1="16" y1="13" x2="8" y2="13" data-id="element-9077" data-mp="1jd6torqya0i1"></line>
          <line x1="16" y1="17" x2="8" y2="17" data-id="element-9078" data-mp="1oap6it838zvb"></line>
          <polyline points="10 9 9 9 8 9" data-id="element-9079" data-mp="1oagut4npc0lp"></polyline>
        </svg>
        <span className="text-[8px] font-bold text-white truncate" data-id="element-9080" data-mp="14oexxo4ycxyh">
          Report generated
        </span>
      </div>
      <div className="p-2.5 flex-1 flex flex-col" data-id="element-9081" data-mp="10owmrk2xo18u">
        <div className="text-[9.5px] font-bold text-neutral-900 leading-tight mb-1.5" data-id="element-9082" data-mp="1encr54dbpfnt">
          Weekly Sales & ROAS
        </div>
        <div className="space-y-1" data-id="element-9083" data-mp="19n8btho5iqz6">
          <div className="h-1 w-full rounded bg-neutral-100" data-id="element-9084" data-mp="19myndyir8rvl" />
          <div className="h-1 w-[92%] rounded bg-neutral-100" data-id="element-9085" data-mp="1nmzlj67mxl7d" />
          <div className="h-1 w-3/4 rounded bg-neutral-100" data-id="element-9086" data-mp="1jmfo01j5gfkp" />
        </div>
        <div className="mt-auto flex items-center gap-1" data-id="element-9087" data-mp="1jkd1vsl4b6l9">
          <span className="flex items-center justify-center w-3 h-3 rounded-full bg-[#0F9D58] shrink-0" data-id="element-9088" data-mp="1xcsus0kb1o8g">
            <CheckIcon size={8} strokeWidth={4} className="text-white" data-id="element-9089" data-mp="1sg2h1jvz34q7" />
          </span>
          <span className="text-[8px] font-semibold text-[#0F9D58]" data-id="element-9090" data-mp="27d3apsxq59rb">
            Saved to Notion
          </span>
        </div>
      </div>
    </div>;
}
function EcomToastCard() {
  return <div className={`${ECOM_FT_CARD_BASE} justify-center px-3`} data-id="element-9091" data-mp="33s5m8s65m4qi">
      <span className="flex items-center justify-center w-9 h-9 rounded-full bg-[#0F9D58] shrink-0 mb-2.5" data-id="element-9092" data-mp="33p9nj1mhfc10">
        <CheckIcon size={18} strokeWidth={3.5} className="text-white" data-id="element-9093" data-mp="2uqxnya3n13wf" />
      </span>
      <div className="text-[11px] font-bold text-neutral-900 leading-tight" data-id="element-9094" data-mp="2ut201fbox97s">
        Ad sets paused
      </div>
      <p className="mt-1 text-[9.5px] text-neutral-500 leading-snug" data-id="element-9095" data-mp="2pu7c5yyv8ugt">
        3 underperforming ad sets paused in Meta Ads.
      </p>
      <div className="mt-2 flex items-center gap-1" data-id="element-9096" data-mp="2g1t3snda8plo">
        <span className="w-1 h-1 rounded-full bg-[#0F9D58]" data-id="element-9097" data-mp="2fzyllgtebbla" />
        <span className="text-[8px] font-semibold text-[#0F9D58]" data-id="element-9098" data-mp="2b3rn293bx2dd">
          Completed just now
        </span>
      </div>
    </div>;
}
function EcomFinanceCard() {
  return <div className={ECOM_FT_CARD_BASE} data-id="element-9099" data-mp="222tgbj1iagr3">
      <div className="flex items-center justify-between px-2 py-1.5 bg-neutral-50 border-b border-neutral-100 shrink-0" data-id="element-9100" data-mp="221qrdgnquodt">
        <span className="flex items-center gap-1 shrink-0" data-id="element-9101" data-mp="1x5a22j4t63z5">
          <span className="w-1.5 h-1.5 rounded-full bg-[#FF5F57]" data-id="element-9102" data-mp="ck3csexbaixx" />
          <span className="w-1.5 h-1.5 rounded-full bg-[#FEBC2E]" data-id="element-9103" data-mp="hjzfmau9w4bw" />
          <span className="w-1.5 h-1.5 rounded-full bg-[#28C840]" data-id="element-9104" data-mp="hmc4l7yg7eyg" />
        </span>
        <span className="text-[7px] font-bold text-neutral-400" data-id="element-9105" data-mp="mju8ul1it604">Inventory</span>
      </div>
      <div className="p-2.5 flex-1 flex flex-col" data-id="element-9106" data-mp="qke23diz3pgz">
        <div className="text-[10px] font-bold text-neutral-900 leading-tight mb-2" data-id="element-9107" data-mp="3q0azs7dliwaw">
          Stock updated
        </div>
        <div className="space-y-1.5" data-id="element-9108" data-mp="3uycll02ep9q2">
          {[{
          label: 'Items updated',
          value: '42'
        }, {
          label: 'Low stock alerts',
          value: '3'
        }].map(r => <div key={r.label} className="flex items-center justify-between gap-2" data-id="element-9109" data-mp="7mlcte19ve22">
              <span className="text-[8px] text-neutral-500 truncate" data-id="element-9110" data-mp="379t9sak5eet8">
                {r.label}
              </span>
              <span className="text-[8px] font-bold text-neutral-800 shrink-0" data-id="element-9111" data-mp="3bad7pgcjpmgf">
                {r.value}
              </span>
            </div>)}
        </div>
        <div className="mt-auto flex items-center gap-1" data-id="element-9112" data-mp="ieli2olwijxw">
          <span className="flex items-center justify-center w-3 h-3 rounded-full bg-[#0F9D58] shrink-0" data-id="element-9113" data-mp="dgjubqdj5wg3">
            <CheckIcon size={8} strokeWidth={4} className="text-white" data-id="element-9114" data-mp="s56d622d96c2" />
          </span>
          <span className="text-[8px] font-semibold text-[#0F9D58]" data-id="element-9115" data-mp="n8fwpdmng8q4">
            Synced · Google Sheets
          </span>
        </div>
      </div>
    </div>;
}
function EcomFollowsThroughVisual() {
  const reduced = !!useReducedMotion();
  const cards = [<EcomEmailCard key="email" data-id="element-9116" data-mp="3qqg9d635h2a2" />, <EcomReportCard key="report" data-id="element-9117" data-mp="8j09u8q8onfp" />, <EcomToastCard key="toast" data-id="element-9118" data-mp="8ja2k42o58p3" />, <EcomFinanceCard key="finance" data-id="element-9119" data-mp="8g5n2ajr6qj4" />];
  return <div className="flex-1 relative mt-6 overflow-hidden flex items-center" style={{
    WebkitMaskImage: 'linear-gradient(to right, transparent 0, #000 4%, #000 96%, transparent 100%)',
    maskImage: 'linear-gradient(to right, transparent 0, #000 4%, #000 96%, transparent 100%)'
  }} data-id="element-9120" data-mp="4gnd29tgzzv2">
      {reduced ? <div className="flex gap-4 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-9121" data-mp="1nfbi93ig8kdn">
          {cards}
        </div> : <motion.div className="flex gap-4 w-max" animate={{
      x: ['-50%', '0%']
    }} transition={{
      duration: 26,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-9122" data-mp="1sfzb7uq8nbai">
          {[...cards, ...cards].map((card, i) => cloneElement(card, {
        key: `ft-${i}`
      }))}
        </motion.div>}
    </div>;
}
const ECOM_RECALLED = ['The 20% discount code from BFCM', 'Top performing ad creative', 'Inventory levels from last year'];
function EcomRemembersVisual() {
  return <div className="flex-1 relative mt-6 overflow-hidden" data-id="element-9123" data-mp="1wfhtsjul7aa6">
      <div className="absolute inset-0 flex items-stretch justify-center" data-id="element-9124" data-mp="21f2squ9rbimw">
        <div className="w-full max-w-[280px] self-center rounded-2xl bg-white border border-neutral-200 shadow-[0_18px_40px_-18px_rgba(43,33,26,0.3)] overflow-hidden" data-id="element-9125" data-mp="21d9nnjesl6h0">
          <div className="bg-[#3F0E40] px-3 py-2 flex items-center gap-1.5" data-id="element-9126" data-mp="2twf3ts4pstof">
            <HashIcon size={11} className="text-white/70 shrink-0" data-id="element-9127" data-mp="167lng3arv8oy" />
            <span className="text-[11px] font-bold text-white truncate" data-id="element-9128" data-mp="1188vj451nbfi">
              bfcm-planning
            </span>
          </div>
          <div className="p-3 space-y-3" data-id="element-9129" data-mp="wa78k84hyryr">
            <motion.div className="flex items-start gap-2" initial={{
            opacity: 0,
            y: 8
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4
          }} data-id="element-9130" data-mp="wb8p2s4a86nv">
              <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/ChatGPT_Image_Apr_24,_2026,_12_02_55_PM_3.webp" alt="Faith" className="w-6 h-6 rounded-md object-cover shrink-0" loading="lazy" data-id="element-9131" data-mp="1k0the1mbsg9t" />

              <div className="min-w-0" data-id="element-9132" data-mp="27pm5jbwphjtv">
                <div className="flex items-baseline gap-1.5" data-id="element-9133" data-mp="22xunjprm8mfc">
                  <span className="text-[11px] font-bold text-neutral-900 leading-none" data-id="element-9134" data-mp="22w9r1ulqqteu">
                    Faith
                  </span>
                  <span className="text-[9px] text-neutral-400 leading-none" data-id="element-9135" data-mp="2f7akrgmt81ll">
                    10:02
                  </span>
                </div>
                <p className="mt-1 text-[11px] text-neutral-700 leading-snug" data-id="element-9136" data-mp="2k5lu7q970a3a">
                  <span className="font-semibold text-[#1264A3] bg-[#1264A3]/10 rounded px-0.5" data-id="element-9137" data-mp="2p6t3jtqzhhc2">
                    @Giga
                  </span>{' '}
                  what worked best for us last BFCM?
                </p>
              </div>
            </motion.div>

            <motion.div className="flex items-start gap-2" initial={{
            opacity: 0,
            y: 8
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: 0.35
          }} data-id="element-9138" data-mp="32wwjk2z9set2">
              <span className="w-6 h-6 rounded-md bg-[#EA580C] flex items-center justify-center shrink-0" data-id="element-9139" data-mp="3hvqjhq7q321n">
                <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="Giga" className="w-3.5 h-3.5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-9140" data-mp="2s7bdl40mnchn" />
              </span>
              <div className="min-w-0 flex-1" data-id="element-9141" data-mp="2x53885snpcpj">
                <div className="flex items-baseline gap-1.5" data-id="element-9142" data-mp="2x4bhb8a0835l">
                  <span className="text-[11px] font-bold text-neutral-900 leading-none" data-id="element-9143" data-mp="3avzw5jbn15q9">
                    Giga
                  </span>
                  <span className="text-[8px] font-bold text-[#616061] bg-neutral-200/80 rounded px-1 py-0.5 leading-none uppercase tracking-wide" data-id="element-9144" data-mp="36011jdzpurhe">
                    App
                  </span>
                </div>
                <div className="mt-1.5 rounded-lg border border-neutral-200 bg-[#FAF8F4] p-2.5" data-id="element-9145" data-mp="1qh69vjf009c8">
                  <div className="flex items-center gap-1 text-[9px] font-bold text-[#EA580C] uppercase tracking-wide mb-1.5" data-id="element-9146" data-mp="1li33nvna7x2o">
                    <Sparkles size={9} data-id="element-9147" data-mp="zix8n84mdyz" /> Recalled from last year
                  </div>
                  <ul className="space-y-1" data-id="element-9148" data-mp="5yvorl9y097w">
                    {ECOM_RECALLED.map((item, i) => <motion.li key={item} className="flex items-start gap-1.5 text-[10px] text-neutral-700 leading-snug" initial={{
                    opacity: 0,
                    x: -6
                  }} whileInView={{
                    opacity: 1,
                    x: 0
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 0.3,
                    delay: 0.6 + i * 0.12
                  }} data-id="element-9149" data-mp="60yf66f0c4d5" data-mpi={i}>
                        <CheckIcon size={11} strokeWidth={3} className="text-[#0F9D58] shrink-0 mt-px" data-id="element-9150" data-mp="34ivo23fm130u" />

                        <span data-id="element-9151" data-mp="39hyk4ydm3ywo">{item}</span>
                      </motion.li>)}
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>;
}
function EcommerceBenefits() {
  return <section className="w-full bg-[#F4F0EA] py-24 md:py-32 border-y border-neutral-200/70 overflow-hidden" data-id="element-9152" data-mp="3qnac5vnnldwx">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-9153" data-mp="3mmi2506yj4zl">
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-20" data-id="element-9154" data-mp="3hugpve9she9k">
          <span className="inline-flex items-center gap-2.5 mb-5 md:mb-6 rounded-full border border-[#EA580C]/20 bg-[#EA580C]/[0.06] pl-2.5 pr-3.5 py-1.5" data-id="element-9155" data-mp="3hsm27s74v81s">
            <span className="relative flex h-2 w-2 shrink-0" data-id="element-9156" data-mp="37xls4xlcbhwc">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#EA580C] opacity-75" data-id="element-9157" data-mp="32xfy5arlljb3" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#EA580C]" data-id="element-9158" data-mp="2yvcjyedjo0m4" />
            </span>
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-[#C2410C]" data-id="element-9159" data-mp="2txjg3rzprip0">
              Get Early Access
            </span>
          </span>
          <h2 className="text-3xl md:text-5xl font-title font-medium text-[#2B211A] tracking-tight leading-[1.1] mb-5 md:mb-6" data-id="element-9160" data-mp="x254n4xxlj08">
            Meet your most capable hire
          </h2>
          <p className="text-lg md:text-xl text-neutral-500 leading-relaxed max-w-2xl mx-auto" data-id="element-9161" data-mp="zonrpt41hpvf">
            A proactive multiplayer agent that connects to all your tools, works
            in your workspace, and executes with all the context.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto" data-id="element-9162" data-mp="1ecii32ukri5c">
          <div className="md:col-span-2 bg-[#FAF8F4] rounded-[2rem] p-6 sm:p-8 md:p-12 border border-neutral-200/60 shadow-sm flex flex-col md:flex-row items-center gap-5 md:gap-8 overflow-hidden" data-id="element-9163" data-mp="gquyxjn4qwbr">
            <div className="flex-1 text-center md:text-left" data-id="element-9164" data-mp="qnzkpid2cps1">
              <h3 className="text-xl sm:text-2xl md:text-3xl font-title font-medium text-[#2B211A] mb-3" data-id="element-9165" data-mp="qqlpxbchet73">
                <span className="sm:hidden" data-id="element-9166" data-mp="223ehal1d1fmm">Works where you work</span>
                <span className="hidden sm:inline" data-id="element-9167" data-mp="2234nemd72obn">
                  Works where you already are
                </span>
              </h3>
              <p className="text-neutral-500 text-base sm:text-lg" data-id="element-9168" data-mp="2yzkswnole5hl">
                Giga lives in Slack and soon Teams, your messaging apps, and
                your email.
              </p>
            </div>
            <div className="w-full shrink-0 md:flex-1 relative h-[340px] sm:h-[380px] rounded-2xl overflow-hidden flex justify-center items-center border border-neutral-100" data-id="element-9169" data-mp="37zhdoe7v2k7d">
              <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Untitled_design_(6)_1.webp" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" style={{
              objectPosition: '60% 50%'
            }} loading="lazy" data-id="element-9170" data-mp="3hpsswwwzwftk" />

              <div aria-hidden="true" className="absolute inset-0" style={{
              background: 'radial-gradient(70% 70% at 50% 70%, transparent 0%, rgba(43,33,26,0.18) 100%)'
            }} data-id="element-9171" data-mp="3voaf2q795k9d" />

              <EcomWorksWhereYouWorkVisual data-id="element-9172" data-mp="21cc5gn516mcy" />
            </div>
          </div>

          <div className="bg-[#FAF8F4] rounded-[2rem] p-6 sm:p-8 border border-neutral-200/60 shadow-sm flex flex-col overflow-hidden h-[340px] sm:h-[380px]" data-id="element-9173" data-mp="1tsb9g8kwo09r">
            <div className="text-center md:text-left relative z-20" data-id="element-9174" data-mp="1ozi3jhdpalw3">
              <h3 className="text-xl sm:text-2xl font-title font-medium text-[#2B211A] mb-3" data-id="element-9175" data-mp="1k2ro8w9pzubq">
                Self onboards in seconds
              </h3>
              <p className="text-neutral-500 text-sm sm:text-base" data-id="element-9176" data-mp="2crfgxcwstlzb">
                Once connected, Giga gets up to speed.
              </p>
            </div>
            <EcomConnectsVisual data-id="element-9177" data-mp="27sm4lnt9010q" />
          </div>

          <div className="bg-[#FAF8F4] rounded-[2rem] p-6 sm:p-8 border border-neutral-200/60 shadow-sm flex flex-col overflow-hidden h-[340px] sm:h-[380px]" data-id="element-9178" data-mp="23pfyqha1sbus">
            <div className="text-center md:text-left relative z-20" data-id="element-9179" data-mp="1yth3qau7rvle">
              <h3 className="text-xl sm:text-2xl font-title font-medium text-[#2B211A] mb-3" data-id="element-9180" data-mp="scnahgggu5jh">
                Acts on your behalf
              </h3>
              <p className="text-neutral-500 text-sm sm:text-base" data-id="element-9181" data-mp="otedz3feu9zd">
                Giga doesn't just reply — it does the work and hands back real
                results.
              </p>
            </div>
            <EcomPartnerVisual data-id="element-9182" data-mp="fszpxwb2ags6" />
          </div>

          <div className="bg-[#FAF8F4] rounded-[2rem] p-6 sm:p-8 border border-neutral-200/60 shadow-sm flex flex-col overflow-hidden h-[340px] sm:h-[380px]" data-id="element-9183" data-mp="fs6rrjq64zln">
            <div className="text-center md:text-left relative z-20" data-id="element-9184" data-mp="62dg6iq10khe">
              <h3 className="text-xl sm:text-2xl font-title font-medium text-[#2B211A] mb-3" data-id="element-9185" data-mp="atdbxkneswib">
                Never out of office
              </h3>
              <p className="text-neutral-500 text-sm sm:text-base" data-id="element-9186" data-mp="3sb6b0w8elk4f">
                A colleague who's always on — replying, nudging, and noticing,
                even when you're not.
              </p>
            </div>
            <EcomFollowsThroughVisual data-id="element-9187" data-mp="3j9yp7dn2qbr8" />
          </div>

          <div className="bg-[#FAF8F4] rounded-[2rem] p-6 sm:p-8 border border-neutral-200/60 shadow-sm flex flex-col overflow-hidden h-[340px] sm:h-[380px]" data-id="element-9188" data-mp="3o8jfak8m3f1y">
            <div className="text-center md:text-left relative z-20" data-id="element-9189" data-mp="34ob51sgvf78d">
              <h3 className="text-xl sm:text-2xl font-title font-medium text-[#2B211A] mb-3" data-id="element-9190" data-mp="2zoy5q88tc7ow">
                Remembers everything
              </h3>
              <p className="text-neutral-500 text-sm sm:text-base" data-id="element-9191" data-mp="39lt48r536836">
                Context carries over so you never have to explain twice.
              </p>
            </div>
            <EcomRemembersVisual data-id="element-9192" data-mp="3nizjwcuijqo6" />
          </div>
        </div>
      </div>
    </section>;
}
// --- CUSTOM E-COMMERCE PROOF ---
function EcommerceProof() {
  const testimonials = [{
    quote: 'I used to spend hours digging through Shopify and Gorgias to figure out why a customer was upset. Now I just ask Giga for the complete history, and it gives me the full picture in seconds.',
    name: 'Sarah Jenkins',
    role: 'Head of CX, DTC Apparel Brand',
    img: 'https://i.pravatar.cc/150?img=44'
  }, {
    quote: "Instead of opening Meta Ads, Google Analytics, and Shopify to understand yesterday's performance, I just ask Giga. It's like having a data analyst who never sleeps.",
    name: 'Marcus Thorne',
    role: 'Performance Marketing Lead',
    img: 'https://i.pravatar.cc/150?img=11'
  }, {
    quote: "As a solo founder, I can't remember every decision we made last Black Friday. Giga remembers everything and surfaces the exact Notion docs and Slack threads I need.",
    name: 'Elena Rodriguez',
    role: 'Founder, Skincare Co.',
    img: 'https://i.pravatar.cc/150?img=5'
  }];
  return <section className="w-full bg-[#1A1410] py-24 md:py-32 text-white" data-id="element-9193" data-mp="1lpwh9xyvfljv">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-9194" data-mp="1pnu4iv92zxkm">
        <div className="text-center mb-16" data-id="element-9195" data-mp="1uozz6ymaxuws">
          <h2 className="text-3xl md:text-5xl font-title font-medium tracking-tight mb-6" data-id="element-9196" data-mp="1uprv6ajt9unl">
            How lean brands punch above their weight
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-9197" data-mp="362lz9etp9gx3">
          {testimonials.map((t, i) => <div key={i} className="bg-white/5 rounded-2xl p-8 border border-white/10 flex flex-col" data-id="element-9198" data-mp="3g7tzsnz8nbce" data-mpi={i}>
              <p className="text-lg text-white/90 leading-relaxed mb-8 flex-1" data-id="element-9199" data-mp="3b9im225togow">
                "{t.quote}"
              </p>
              <div className="flex items-center gap-4" data-id="element-9200" data-mp="36g7lujvimy5y">
                <img src={t.img} alt={t.name} className="w-12 h-12 rounded-full" data-id="element-9201" data-mp="32gp602ijxd9z" />

                <div data-id="element-9202" data-mp="2xgt0tzy4bfsu">
                  <div className="font-bold text-white" data-id="element-9203" data-mp="2xhcgtb47ju48">{t.name}</div>
                  <div className="text-sm text-white/60" data-id="element-9204" data-mp="2shzm6473hir3">{t.role}</div>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}
export function EcommerceHome() {
  return <>
      <EcommerceHero data-id="element-9205" data-mp="2jht9uln4uoti" />
      <EcommerceConnections data-id="element-9206" data-mp="2jicre18vfb70" />
      <EcommerceBenefits data-id="element-9207" data-mp="2jj4hiwjp8gpw" />
      <EcommerceProblem data-id="element-9208" data-mp="lrjksj37qcyo" />
      <EcommerceScenario data-id="element-9209" data-mp="lrtgmnaxveca" />
      <EcommerceSteps data-id="element-9210" data-mp="lr057911c0nt" />
      <EcommerceProof data-id="element-9211" data-mp="lpfj87ssdq1k" />
      <ClosingCTA data-id="element-9212" data-mp="hq7v6dkc9rqh" />
    </>;
}